import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Send, Loader2, CheckCircle, Download, Upload, Sparkles, User, Undo2, Redo2 } from 'lucide-react'
import { useWorldSimulatorStore } from '../stores/worldSimulatorStore'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import { RealAIService } from '../services/realAiService'
import worldTemplateService from '../services/worldTemplateService'
import aiPersonalityService from '../services/aiPersonalityService'
import toast from 'react-hot-toast'

// 世界创建步骤
const CREATION_STEPS = [
  { id: 1, field: 'basic', title: '基本信息', description: '世界名称、描述和概要' },
  { id: 2, field: 'worldview', title: '世界观设定', description: '世界的核心设定和背景' },
  { id: 3, field: 'systems', title: '系统与规则', description: '世界规则、量化系统' },
  { id: 4, field: 'regions', title: '地区设定', description: '主要地区和场所' },
  { id: 5, field: 'characters', title: '角色设定', description: '重要NPC和角色' },
  { id: 6, field: 'confirm', title: '确认发布', description: '预览并发布世界' }
]

const CHARACTER_PROGRESS_FIELDS = [
  { key: 'hasName', label: '姓名' },
  { key: 'hasRole', label: '身份/职业' },
  { key: 'hasTraits', label: '性格特点' },
  { key: 'hasMotivation', label: '目标' },
  { key: 'hasBackground', label: '人物背景' }
]

export default function WorldGuidedCreation({ onComplete }) {
  const navigate = useNavigate()
  const { loadWorlds } = useWorldSimulatorStore()
  const messagesEndRef = useRef(null)
  
  // 状态
  const [currentStep, setCurrentStep] = useState(1)
  const [messages, setMessages] = useState([])
  const [userInput, setUserInput] = useState('')
  const [isAIThinking, setIsAIThinking] = useState(false)
  
  // 最近一次修改的上下文（用于纠错）
  const [lastModification, setLastModification] = useState(null) // { field, value, label }
  
  // 导入进度状态
  const [importProgress, setImportProgress] = useState(null)
  const [showImportProgress, setShowImportProgress] = useState(false)
  const [quickReplies, setQuickReplies] = useState([])
  const [isAutoCreating, setIsAutoCreating] = useState(false) // 一键创建状态
  const [showImportDialog, setShowImportDialog] = useState(false) // 导入对话框
  const [highlightedFields, setHighlightedFields] = useState([]) // 高亮显示的字段
  const [updatedFields, setUpdatedFields] = useState(new Set()) // 已更新的字段集合
  const [showPersonalitySelector, setShowPersonalitySelector] = useState(false) // 性格选择器
  const [currentPersonality, setCurrentPersonality] = useState(aiPersonalityService.getCurrentPersonality())
  
  // 编辑状态管理
  const [editingField, setEditingField] = useState(null) // 当前正在编辑的字段 {type: 'name'|'description'|..., value: string}
  const [editingRegionIndex, setEditingRegionIndex] = useState(null) // 正在编辑的地区索引，-1表示添加新地区
  const [editingCharacterIndex, setEditingCharacterIndex] = useState(null) // 正在编辑的角色索引，-1表示添加新角色
  const [editingQuantitativeSystemIndex, setEditingQuantitativeSystemIndex] = useState(null) // 正在编辑的量化系统索引，-1表示添加新系统
  const sanitizeGeneratedContent = (text, personality) => {
    if (!text) return text
    let cleaned = text
    if (personality?.avatar) {
      const avatarRegex = new RegExp(`^\\s*${personality.avatar}\\s*`, 'g')
      cleaned = cleaned.replace(avatarRegex, '')
    }
    if (personality?.name) {
      const nameRegex = new RegExp(`^\\s*${personality.name}[：:，,\\s]*`, 'g')
      cleaned = cleaned.replace(nameRegex, '')
    }
    cleaned = cleaned.replace(/^(好的|嗯嗯|嗯|好啊)[！!~,，。.\s]*/g, '')
    cleaned = cleaned.replace(/^["“”]+/, '').trim()
    const lines = cleaned.split('\n').filter(line => {
      const trimmed = line.trim()
      if (!trimmed) return true
      if (personality?.avatar && trimmed.startsWith(personality.avatar)) return false
      if (personality?.name && trimmed.startsWith(personality.name)) return false
      if (/^(好的|嗯嗯|嗯|没问题|行的|了解)/.test(trimmed) && trimmed.length < 20) return false
      return true
    })
    return lines.join('\n').trim()
  }
  const [editingSystemKey, setEditingSystemKey] = useState(null) // 正在编辑的系统键名，'__new__'表示添加新系统
  const [editingRuleIndex, setEditingRuleIndex] = useState(null) // 正在编辑的规则索引，-1表示添加新规则
  
  // 撤销/重做历史记录
  const [history, setHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  
  // 自动保存状态
  const [lastSaveTime, setLastSaveTime] = useState(null)
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true)
  
  // 世界数据
  const [worldData, setWorldData] = useState({
    name: '',
    description: '',
    summary: '',
    preText: '',
    postText: '',
    prePrompt: '',
    worldBook: [],
    systems: {}, // 包含规则和量化系统
    quantitativeSystems: [],
    characters: [], // 角色设定
    regions: [], // 地区列表
    language: 'zh-Hans'
  })

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z 撤销
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }
      // Ctrl+Y 或 Ctrl+Shift+Z 重做
      if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
        e.preventDefault()
        handleRedo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [historyIndex, history])

  // 自动保存草稿
  useEffect(() => {
    if (!autoSaveEnabled) return
    
    const autoSaveInterval = setInterval(() => {
      // 只有在有数据时才保存
      if (worldData.name || worldData.description || messages.length > 1) {
        try {
          localStorage.setItem('world-creation-draft', JSON.stringify(worldData))
          localStorage.setItem('world-creation-messages', JSON.stringify(messages))
          localStorage.setItem('world-creation-step', currentStep.toString())
          setLastSaveTime(new Date())
        } catch (error) {
          console.error('自动保存失败:', error)
        }
      }
    }, 30000) // 每30秒保存一次

    return () => clearInterval(autoSaveInterval)
  }, [worldData, messages, currentStep, autoSaveEnabled])

  // 初始化对话
  useEffect(() => {
    // 尝试恢复草稿
    const savedDraft = localStorage.getItem('world-creation-draft')
    const savedMessages = localStorage.getItem('world-creation-messages')
    const savedStep = localStorage.getItem('world-creation-step')
    
    if (savedDraft && savedMessages) {
      const shouldRestore = window.confirm('检测到未完成的创作草稿，是否继续？')
      if (shouldRestore) {
        try {
          setWorldData(JSON.parse(savedDraft))
          setMessages(JSON.parse(savedMessages))
          if (savedStep) setCurrentStep(parseInt(savedStep))
          toast.success('已恢复草稿')
          return
        } catch (error) {
          console.error('恢复草稿失败:', error)
        }
      } else {
        // 清除草稿
        localStorage.removeItem('world-creation-draft')
        localStorage.removeItem('world-creation-messages')
        localStorage.removeItem('world-creation-step')
      }
    }
    
    initConversation()
  }, [])

  const initConversation = async () => {
    // 设置初始步骤为1（基础信息）
    setCurrentStep(1)
    
    // 检查是否有从首页传递过来的世界创建描述
    const creationDescription = sessionStorage.getItem('world-creation-description')
    
    if (creationDescription) {
      // 如果有初始描述，自动将描述添加到世界数据中
      sessionStorage.removeItem('world-creation-description')
      setWorldData(prev => ({
        ...prev,
        description: creationDescription
      }))
    }
    
    // 确保消息列表为空，避免重复显示
    setMessages([])
    
    // 使用askNextStepQuestion来开始引导流程
    askNextStepQuestion()
  }

  // 发送用户消息
  const handleSendMessage = async () => {
    if (!userInput.trim() || isAIThinking) return

    const trimmedInput = userInput.trim()
    
    // 如果只有一个"继续"选项，且用户输入"继续"，直接触发快捷回复
    if (quickReplies.length === 1 && quickReplies[0] === '继续' && trimmedInput === '继续') {
      handleQuickReply('继续')
      setUserInput('')
      return
    }

    const userMsg = {
      role: 'user',
      content: trimmedInput,
      timestamp: Date.now()
    }

    setMessages(prev => [...prev, userMsg])
    setUserInput('')
    setQuickReplies([])
    
    // 处理AI响应
    await processUserInput(userMsg.content)
  }

  // 处理用户输入
  const processUserInput = async (input) => {
    setIsAIThinking(true)
    
    try {
      const currentStepData = CREATION_STEPS[currentStep - 1]
      
      // 首先使用AI理解用户意图（对话模式）
      // 检查是否是用户主动发起的讨论或修改请求
      const isInitialInput = currentStep === 1 && (!worldData.description || !worldData.name)
      const isUserInitiatedDiscussion = !input.match(/^(?:继续|选择|选|用|要)(?:第)?(\d+)?/) && 
                                        (input.includes('改') || input.includes('修改') || input.includes('想') || 
                                         input.includes('要') || input.includes('帮') || input.includes('增加') || 
                                         input.includes('添加') || input.length > 10)
      
      if (isUserInitiatedDiscussion && !isInitialInput) {
        // 使用AI理解用户意图并进入对话模式
        const personality = currentPersonality
        const discussionPrompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

【对话模式】
用户主动发起了对话，你需要切换到对话模式，理解用户的需求并帮助完成。

用户输入：${input}

当前世界信息：
- 名称：${worldData.name || '未设置'}
- 描述：${worldData.description || '暂无'}
- 世界观：${worldData.preText?.substring(0, 200) || '暂无'}...

当前步骤：${currentStepData.title}（${currentStepData.description}）

你的任务：
1. 仔细理解用户的真实意图和需求
2. 如果用户想要修改内容（如"我想改一个名字"、"帮我改名字"、"想要改名字"等），必须基于现有世界设定生成5-8个合适的建议
3. 如果用户想要增加内容（如角色、地区、规则），提供帮助选项
4. 用引导员的说话方式，自然对话，像朋友一样帮助用户

重要规则：
- 如果用户说"我想改名字"、"我想要改一个名字"、"帮我改名字"等，必须识别为修改名字的请求，生成建议
- 不要只是提示用户手动输入，要主动生成建议帮助用户
- 建议要符合世界风格，有画面感

如果是修改名字的请求，请以JSON格式返回：
{
  "intent": "modify",
  "field": "name",
  "label": "世界名称",
  "suggestions": ["名称1", "名称2", "名称3", "名称4", "名称5", "名称6", "名称7"],
  "message": "用引导员的说话方式，简短说明（1-2句话）"
}

如果是修改描述/概要的请求，请以JSON格式返回：
{
  "intent": "modify",
  "field": "description|summary",
  "label": "世界描述|世界概要",
  "suggestions": ["建议1", "建议2", "建议3", "建议4"],
  "message": "用引导员的说话方式，简短说明（1-2句话）"
}

如果是增加内容的请求，请以JSON格式返回：
{
  "intent": "add",
  "type": "character|region|rule",
  "label": "角色|地区|规则",
  "message": "用引导员的说话方式，提供帮助选项"
}

如果是其他讨论，请以JSON格式返回：
{
  "intent": "discuss",
  "message": "用引导员的说话方式，理解并回应用户的讨论"
}

直接返回JSON，不要其他内容。`

        try {
          const aiResponse = await RealAIService.callLanguageModel(discussionPrompt)
          const jsonMatch = aiResponse.match(/\{[\s\S]*\}/)
          
          if (jsonMatch) {
            const result = JSON.parse(jsonMatch[0])
            
            if (result.intent === 'modify' && result.suggestions && result.suggestions.length > 0) {
              // 用户想要修改，AI已生成建议
              const suggestions = result.suggestions
              const message = result.message || '好的，我为你想了几个建议：'
              const field = result.field
              const label = result.label
              
              // 构建建议消息
              const suggestionText = suggestions.map((s, i) => `${i + 1}. ${s}`).join('\n')
              
              let content = `${personality.avatar} ${message}\n\n${suggestionText}\n\n`
              content += `你可以：\n• 选择一个建议（告诉我编号或直接输入名称）\n• 自己输入想要的内容`
              
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: content,
                timestamp: Date.now()
              }])
              
              // 提供快捷回复选项
              const quickReplies = suggestions.slice(0, 3)
              setQuickReplies(quickReplies)
              
              setIsAIThinking(false)
              return
            } else if (result.intent === 'add') {
              // 用户想要增加内容
              const personality = currentPersonality
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `${personality.avatar} ${result.message || `好的，我来帮你${result.label === '角色' ? '添加角色' : result.label === '地区' ? '添加地区' : '添加规则'}。`}\n\n你可以选择：\n• 让我来帮你完成（我会基于现有世界设定自动生成）\n• 你告诉我（你直接描述，我来整理）`,
                timestamp: Date.now()
              }])
              
              setQuickReplies(['继续', '你告诉我'])
              setIsAIThinking(false)
              return
            } else if (result.intent === 'discuss') {
              // 其他讨论
              const personality = currentPersonality
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `${personality.avatar} ${result.message}`,
                timestamp: Date.now()
              }])
              
              setQuickReplies(['继续'])
              setIsAIThinking(false)
              return
            }
          }
        } catch (error) {
          console.error('AI理解意图失败:', error)
          // 如果AI理解失败，继续原有的处理逻辑
        }
      }
      
      // 先检查是否是纠错指令（纠正上一次的修改）
      if (lastModification) {
        const correctionPatterns = [
          /(?:多了|多一个|多出|多余)(.+?)(?:字)?$/,
          /(?:去掉|删掉|删除|移除|不要)(.+?)(?:字)?$/,
          /(?:没有|不是|不应该有|不包含|不含)(.+?)(?:字)?$/,
          /(?:应该是|正确的是|实际是|是)(.+)$/,
          /(?:改成|换成|变成)(.+)$/
        ]
        
        for (const pattern of correctionPatterns) {
          const match = input.match(pattern)
          if (match && match[1]) {
            const errorText = match[1].trim()
            let correctedValue = lastModification.value
            
            // 检查是否存在要去除的文字
            const hasErrorText = correctedValue.includes(errorText)
            
            // 根据不同的纠错指令处理
            if (input.includes('多了') || input.includes('多一个') || input.includes('多出') || input.includes('多余') ||
                input.includes('去掉') || input.includes('删掉') || input.includes('删除') || 
                input.includes('移除') || input.includes('不要')) {
              // 去掉多余的字
              if (hasErrorText) {
                correctedValue = correctedValue.replace(new RegExp(errorText, 'g'), '')
              } else {
                // 如果找不到该文字，可能是用户描述有误，给出提示
                const personality = currentPersonality
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `${personality.avatar} 抱歉，我在「${lastModification.value}」中没有找到「${errorText}」。\n\n请直接告诉我正确的${lastModification.label}应该是什么？`,
                  timestamp: Date.now()
                }])
                setQuickReplies([])
                setIsAIThinking(false)
                return
              }
            } else if (input.includes('没有') || input.includes('不是') || input.includes('不应该有') || 
                       input.includes('不包含') || input.includes('不含')) {
              // 同样是去掉
              if (hasErrorText) {
                correctedValue = correctedValue.replace(new RegExp(errorText, 'g'), '')
              } else {
                const personality = currentPersonality
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `${personality.avatar} 抱歉，我在「${lastModification.value}」中没有找到「${errorText}」。\n\n请直接告诉我正确的${lastModification.label}应该是什么？`,
                  timestamp: Date.now()
                }])
                setQuickReplies([])
                setIsAIThinking(false)
                return
              }
            } else if (input.includes('应该是') || input.includes('正确的是') || input.includes('实际是') ||
                       input.includes('改成') || input.includes('换成') || input.includes('变成')) {
              // 直接替换
              correctedValue = match[1].trim()
            }
            
            correctedValue = correctedValue.trim()
            
            // 更新数据
            setWorldData(prev => {
              const updated = { ...prev, [lastModification.field]: correctedValue }
              saveToHistory(prev)
              return updated
            })
            
            // 更新最近修改记录
            setLastModification({
              field: lastModification.field,
              value: correctedValue,
              label: lastModification.label
            })
            
            // 触发高亮
            triggerFieldHighlight(lastModification.field === 'preText' ? 'worldview' : lastModification.field)
            
            // 给出确认消息（使用引导员的说话方式）
            const personality = currentPersonality
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `${personality.avatar} 理解了！已将${lastModification.label}修正为：\n\n「${correctedValue.length > 100 ? correctedValue.substring(0, 100) + '...' : correctedValue}」\n\n右侧已更新。这样对吗？`,
              timestamp: Date.now()
            }])
            
            // 设置"继续"选项
            setQuickReplies(['继续'])
            
            setIsAIThinking(false)
            return
          }
        }
      }
      
      // 检查用户是否想要修改但需要AI帮助思考（如"我想改一个名字"、"帮我改名字"）
      const helpModifyPatterns = [
        { 
          pattern: /(?:我想|我要|帮我|帮我思考|帮我改|帮我修改|帮我起|帮我取)(?:一个|个)?(?:名字|名称|世界名称|世界名字)/i, 
          field: 'name', 
          label: '世界名称',
          helpType: 'generate_suggestions'
        },
        { 
          pattern: /(?:我想|我要|帮我|帮我思考|帮我改|帮我修改)(?:一个|个)?(?:描述|世界描述)/i, 
          field: 'description', 
          label: '世界描述',
          helpType: 'generate_suggestions'
        },
        { 
          pattern: /(?:我想|我要|帮我|帮我思考|帮我改|帮我修改)(?:一个|个)?(?:概要|世界概要)/i, 
          field: 'summary', 
          label: '世界概要',
          helpType: 'generate_suggestions'
        }
      ]
      
      for (const { pattern, field, label, helpType } of helpModifyPatterns) {
        if (pattern.test(input)) {
          // 用户想要AI帮助思考修改
          setIsAIThinking(true)
          
          try {
            const personality = currentPersonality
            const currentValue = worldData[field] || ''
            
            // 调用AI生成建议
            let prompt = ''
            if (field === 'name') {
              // 生成名字建议
              prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

用户想要修改世界名称，当前名称：${currentValue || '未设置'}

世界信息：
- 描述：${worldData.description || '暂无'}
- 世界观：${worldData.preText?.substring(0, 200) || '暂无'}...

请基于现有世界设定，生成5-8个合适的世界名称建议（每个名称4-8个字）。输出内容必须是纯粹的设定文本，不要包含任何问候、说明、emoji或引导语。

要求：
1. 名称要符合世界风格和主题
2. 要有画面感和代入感
3. 简洁有力，朗朗上口
4. 建议必须使用客观第三人称设定描述，不得出现引导员口吻、对话或emoji

请以JSON格式返回：
{
  "suggestions": ["名称1", "名称2", "名称3", "名称4", "名称5"],
  "message": "简短说明（1-2句话，用引导员的说话方式）"
}

直接返回JSON，不要其他内容。`
            } else {
              // 生成其他字段的建议
              prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

用户想要修改${label}，当前内容：${currentValue || '未设置'}

世界信息：
- 名称：${worldData.name || '未命名'}
- 描述：${worldData.description || '暂无'}
- 世界观：${worldData.preText?.substring(0, 200) || '暂无'}...

请基于现有世界设定，生成3-5个${label}的建议。输出内容必须是纯粹的设定文本，不要包含任何问候、说明、emoji或引导语。

要求：
1. 要符合世界风格和主题
2. 建议必须使用客观第三人称设定描述，不得出现引导员口吻、对话或emoji

请以JSON格式返回：
{
  "suggestions": ["建议1", "建议2", "建议3"],
  "message": "简短说明（1-2句话，用引导员的说话方式）"
}

直接返回JSON，不要其他内容。`
            }
            
            const response = await RealAIService.callLanguageModel(prompt)
            const jsonMatch = response.match(/\{[\s\S]*\}/)
            
            if (jsonMatch) {
              const result = JSON.parse(jsonMatch[0])
              const suggestions = result.suggestions || []
              const message = result.message || ''
              
              // 构建建议消息
              let suggestionText = ''
              if (suggestions.length > 0) {
                suggestionText = suggestions.map((s, i) => `${i + 1}. ${s}`).join('\n')
              }
              
              const personality = currentPersonality
              let content = `${personality.avatar} ${message || '好的，我为你想了几个建议：'}\n\n`
              
              if (suggestionText) {
                content += `${suggestionText}\n\n`
              }
              
              content += `你可以：\n• 选择一个建议（直接告诉我编号或名称）\n• 自己输入想要的内容\n• 点击"继续"保持当前内容，进入下一步`
              
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: content,
                timestamp: Date.now()
              }])
              
              // 提供快捷回复选项
              const quickReplies = []
              if (suggestions.length > 0) {
                // 添加前3个建议作为快捷回复
                suggestions.slice(0, 3).forEach(s => {
                  quickReplies.push(s)
                })
              }
              quickReplies.push('继续')
              setQuickReplies(quickReplies)
              
              setIsAIThinking(false)
              return
            } else {
              // 如果AI没有返回JSON，提供手动输入选项
              const personality = currentPersonality
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `${personality.avatar} 好的，请直接在输入框告诉我你想要的${label}，或者点击"继续"保持当前内容。`,
                timestamp: Date.now()
              }])
              setQuickReplies(['继续'])
              setIsAIThinking(false)
              return
            }
          } catch (error) {
            console.error('生成建议失败:', error)
            const personality = currentPersonality
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `${personality.avatar} 抱歉，生成建议失败了。请直接在输入框告诉我你想要的${label}，或者点击"继续"保持当前内容。`,
              timestamp: Date.now()
            }])
            setQuickReplies(['继续'])
            setIsAIThinking(false)
            return
          }
        }
      }
      
      // 智能识别直接修改指令（优化正则，更严格的模式，避免误识别）
      // 要求必须以修改关键词开头，避免在正常对话中误识别
      const modifyPatterns = [
        { pattern: /^(?:改名为|修改名称为|名称改为|名字改为|命名为|叫做)[:：]?\s*(.+)/, field: 'name', label: '世界名称' },
        { pattern: /^(?:改名|修改名称|名字叫)[:：]?\s*(.+)/, field: 'name', label: '世界名称' },
        { pattern: /^(?:修改描述为|改描述为|描述改为|描述为|补充描述)[:：]?\s*(.+)/, field: 'description', label: '世界描述' },
        { pattern: /^(?:修改概要为|改概要为|概要改为|补充概要)[:：]?\s*(.+)/, field: 'summary', label: '世界概要' },
        { pattern: /^(?:修改世界观为|改世界观为|世界观改为|补充世界观)[:：]?\s*(.+)/, field: 'preText', label: '世界观' }
      ]
      
      for (const { pattern, field, label } of modifyPatterns) {
        const match = input.match(pattern)
        if (match && match[1]) {
          const newValue = match[1].trim()
          
          // 直接更新数据
          setWorldData(prev => {
            const updated = { ...prev, [field]: newValue }
            saveToHistory(prev)
            return updated
          })
          
          // 记录这次修改（用于后续纠错）
          setLastModification({
            field: field,
            value: newValue,
            label: label
          })
          
          // 触发高亮
          triggerFieldHighlight(field === 'preText' ? 'worldview' : field)
          
          // 智能提示：如果识别的内容较短，完整显示方便用户检查
          const displayValue = newValue.length > 50 ? `${newValue.substring(0, 50)}...` : newValue
          const tip = field === 'name' && newValue.length <= 20 
            ? '\n\n💡 提示：如果名称有误，可以直接告诉我，比如「多了一个X字」或「去掉X」'
            : ''
          
          // 给出确认消息（使用引导员的说话方式）
          const personality = currentPersonality
          
          // 检查当前步骤，决定是否提示继续下一步
          const currentStepData = CREATION_STEPS[currentStep - 1]
          const nextStep = CREATION_STEPS.find(s => s.id === currentStep + 1)
          
          // 判断修改是否完成，询问是否进入下一步
          let continueHint = ''
          if (nextStep) {
            continueHint = `\n\n修改已完成！是否进入下一步（${nextStep.title}）？`
          } else {
            continueHint = `\n\n修改已完成！`
          }
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 已将${label}修改为：\n\n「${displayValue}」${tip}${continueHint}\n\n右侧已更新。`,
            timestamp: Date.now()
          }])
          
          // 如果有下一步，提供"继续"按钮；否则不提供
          if (nextStep) {
            setQuickReplies(['继续'])
          } else {
            setQuickReplies([])
          }
          
          setIsAIThinking(false)
          return
        }
      }
      
      // 检查用户是否选择了建议（通过编号、直接输入建议内容，或点击快捷回复）
      const suggestionMatch = input.match(/^(?:选择|选|用|要)(?:第)?(\d+)(?:个|项|条)?$/)
      const lastAssistantMsg = messages.filter(m => m.role === 'assistant').pop()
      
      // 检查是否是建议选择（编号选择）
      if (suggestionMatch && lastAssistantMsg && lastAssistantMsg.content) {
        const suggestionNum = parseInt(suggestionMatch[1])
        const lines = lastAssistantMsg.content.split('\n')
        const suggestionLine = lines.find(line => line.trim().startsWith(`${suggestionNum}.`))
        
        if (suggestionLine) {
          const selectedValue = suggestionLine.replace(/^\d+\.\s*/, '').trim()
          
          // 确定要修改的字段
          let targetField = 'name'
          let targetLabel = '世界名称'
          if (lastAssistantMsg.content.includes('描述')) {
            targetField = 'description'
            targetLabel = '世界描述'
          } else if (lastAssistantMsg.content.includes('概要')) {
            targetField = 'summary'
            targetLabel = '世界概要'
          }
          
          // 更新数据
          setWorldData(prev => {
            const updated = { ...prev, [targetField]: selectedValue }
            saveToHistory(prev)
            return updated
          })
          
          setLastModification({
            field: targetField,
            value: selectedValue,
            label: targetLabel
          })
          
          triggerFieldHighlight(targetField === 'preText' ? 'worldview' : targetField)
          
          const personality = currentPersonality
          const nextStep = CREATION_STEPS.find(s => s.id === currentStep + 1)
          
          // 判断修改是否完成，询问是否进入下一步
          let continueHint = ''
          if (nextStep) {
            continueHint = `\n\n修改已完成！是否进入下一步（${nextStep.title}）？`
          } else {
            continueHint = `\n\n修改已完成！`
          }
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 好的，已将${targetLabel}设置为：\n\n「${selectedValue}」${continueHint}\n\n右侧已更新。`,
            timestamp: Date.now()
          }])
          
          // 如果有下一步，提供"继续"按钮；否则不提供
          if (nextStep) {
            setQuickReplies(['继续'])
          } else {
            setQuickReplies([])
          }
          setIsAIThinking(false)
          return
        }
      }
      
      // 检查是否是直接输入了建议内容（从最近的消息中提取建议列表，检查输入是否匹配）
      if (lastAssistantMsg && lastAssistantMsg.content && lastAssistantMsg.content.includes('建议')) {
        const lines = lastAssistantMsg.content.split('\n')
        const suggestionLines = lines.filter(line => /^\d+\.\s*/.test(line.trim()))
        
        for (const line of suggestionLines) {
          const suggestionValue = line.replace(/^\d+\.\s*/, '').trim()
          // 检查用户输入是否匹配建议（允许部分匹配，因为用户可能只输入了部分内容）
          if (input.includes(suggestionValue) || suggestionValue.includes(input)) {
            // 确定要修改的字段
            let targetField = 'name'
            let targetLabel = '世界名称'
            if (lastAssistantMsg.content.includes('描述')) {
              targetField = 'description'
              targetLabel = '世界描述'
            } else if (lastAssistantMsg.content.includes('概要')) {
              targetField = 'summary'
              targetLabel = '世界概要'
            }
            
            // 使用完整的建议值
            const selectedValue = suggestionValue
            
            // 更新数据
            setWorldData(prev => {
              const updated = { ...prev, [targetField]: selectedValue }
              saveToHistory(prev)
              return updated
            })
            
            setLastModification({
              field: targetField,
              value: selectedValue,
              label: targetLabel
            })
            
            triggerFieldHighlight(targetField === 'preText' ? 'worldview' : targetField)
            
            const personality = currentPersonality
            const nextStep = CREATION_STEPS.find(s => s.id === currentStep + 1)
            
            // 判断修改是否完成，询问是否进入下一步
            let continueHint = ''
            if (nextStep) {
              continueHint = `\n\n修改已完成！是否进入下一步（${nextStep.title}）？`
            } else {
              continueHint = `\n\n修改已完成！`
            }
            
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `${personality.avatar} 好的，已将${targetLabel}设置为：\n\n「${selectedValue}」${continueHint}\n\n右侧已更新。`,
              timestamp: Date.now()
            }])
            
            // 如果有下一步，提供"继续"按钮；否则不提供
            if (nextStep) {
              setQuickReplies(['继续'])
            } else {
              setQuickReplies([])
            }
            setIsAIThinking(false)
            return
          }
        }
      }
      
      // 检查用户是否要增加角色、地区或规则
      const addIntentPatterns = [
        { 
          pattern: /(?:增加|添加|新增|补充)(?:一个|几个|一些)?(?:角色|人物|NPC)/i, 
          type: 'character',
          label: '角色',
          generateTemplate: '生成1个角色'
        },
        { 
          pattern: /(?:增加|添加|新增|补充)(?:一个|几个|一些)?(?:地区|地点|场所|区域)/i, 
          type: 'region',
          label: '地区',
          generateTemplate: '生成1个地区'
        },
        { 
          pattern: /(?:增加|添加|新增|补充)(?:一个|几条|一些)?(?:规则|系统规则)/i, 
          type: 'rule',
          label: '规则',
          generateTemplate: '生成1条规则'
        }
      ]
      
      let matchedIntent = null
      for (const intent of addIntentPatterns) {
        if (intent.pattern.test(input)) {
          matchedIntent = intent
          break
        }
      }
      
      // 如果识别到要增加内容的意图，提供两个选项
      if (matchedIntent) {
        const personality = currentPersonality
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${personality.avatar} 好的，我来帮你${matchedIntent.label === '角色' ? '添加角色' : matchedIntent.label === '地区' ? '添加地区' : '添加规则'}。\n\n你可以选择：\n• 让我来帮你完成（我会基于现有世界设定自动生成）\n• 你告诉我（你直接描述，我来整理）\n\n💡 点击"继续"默认让我来帮你完成`,
          timestamp: Date.now()
        }])
        
        // 提供快捷回复选项："继续"（默认AI完成）和"你告诉我"
        setQuickReplies(['继续', '你告诉我'])
        setIsAIThinking(false)
        return
      }
      
      // 根据当前步骤，调用AI生成响应和提取信息
      // 第一步时，用户输入想法后，根据想法进行引导优化扩写
      const prompt = buildPromptForStep(currentStepData, input)
      const aiResponse = await RealAIService.callLanguageModel(prompt)
      
      // 解析AI响应
      const parsed = parseAIResponse(aiResponse)
      
      // 更新世界数据
      updateWorldData(currentStepData.field, parsed.extractedData)
      
      // 构建AI消息内容
      let messageContent = parsed.message
      
      // 如果是第一步（基础信息），且已生成内容，增强消息显示优化后的内容摘要
      if (currentStepData.field === 'basic' && parsed.extractedData) {
        const personality = currentPersonality
        const extracted = parsed.extractedData
        
        // 构建优化后的内容摘要
        let summaryParts = []
        if (extracted.name) {
          summaryParts.push(`📝 世界名称：${extracted.name}`)
        }
        if (extracted.description) {
          const desc = extracted.description.length > 100 
            ? extracted.description.substring(0, 100) + '...' 
            : extracted.description
          summaryParts.push(`✨ 世界描述：${desc}`)
        }
        if (extracted.summary) {
          const summ = extracted.summary.length > 100 
            ? extracted.summary.substring(0, 100) + '...' 
            : extracted.summary
          summaryParts.push(`📖 世界概要：${summ}`)
        }
        
        // 如果AI返回的消息太简单，增强它
        if (summaryParts.length > 0 && (!messageContent || messageContent.length < 50)) {
          messageContent = `${personality.avatar} 太好了！我已经为你优化扩展了基础框架：\n\n${summaryParts.join('\n\n')}\n\n完整内容已保存到右侧，你可以查看详情。\n\n💡 如果需要修改，直接在输入框告诉我即可，比如「改名为 XXX」或「修改描述：XXX」`
        } else if (summaryParts.length > 0) {
          // 如果AI已经返回了消息，在消息后追加摘要
          messageContent = `${messageContent}\n\n${summaryParts.join('\n\n')}\n\n完整内容已保存到右侧。`
        }
      }
      
      // 注意：已禁用细化建议功能，保持快速高效的创建流程
      
      // 添加AI消息
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: messageContent,
        timestamp: Date.now()
      }])
      
      // 设置快捷回复：只提供"继续"选项
      setQuickReplies(['继续'])
      
      // 如果该步骤完成，自动进入下一步
      // 但第一步完成后，不立即进入下一步，让用户先查看优化后的内容
      if (parsed.stepComplete && currentStep < CREATION_STEPS.length) {
        if (currentStepData.field === 'basic') {
          // 第一步完成后，不自动进入下一步，等待用户点击"继续"
          // 在消息中提示用户可以继续或修改
          if (!messageContent.includes('继续') && !messageContent.includes('下一步')) {
            const personality = currentPersonality
            setMessages(prev => {
              const lastMsg = prev[prev.length - 1]
              return prev.map((msg, idx) => 
                idx === prev.length - 1 
                  ? {
                      ...msg,
                      content: `${msg.content}\n\n如果内容满意，可以点击"继续"进入下一步（世界观设定）。如果需要修改，直接在输入框告诉我即可。`
                    }
                  : msg
              )
            })
          }
        } else {
          // 其他步骤完成后，自动进入下一步
          setTimeout(() => {
            setCurrentStep(prev => prev + 1)
            askNextStepQuestion()
          }, 1000)
        }
      }
      
    } catch (error) {
      console.error('AI处理失败:', error)
      toast.error('处理失败，请重试')
      const personality = currentPersonality
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${personality.avatar} 抱歉，我遇到了一些问题。请再说一遍？`,
        timestamp: Date.now()
      }])
    } finally {
      setIsAIThinking(false)
    }
  }

  // 构建AI提示词
  const buildPromptForStep = (stepData, userInput) => {
    // 特别处理角色设定步骤
    if (stepData.field === 'characters') {
      return buildCharacterExtractionPrompt(userInput)
    }
    
    // 获取个性化Prompt前缀
    const personalityPrefix = aiPersonalityService.getPersonalityPromptPrefix()
    
    // 确定下一步骤
    const nextStep = CREATION_STEPS.find(s => s.id === stepData.id + 1)
    const nextStepHint = nextStep ? `下一步是：${nextStep.title}（${nextStep.description}）` : '这是最后一个步骤'
    
    // 针对第一步（基础信息）的特殊处理
    let stepSpecificInstructions = ''
    if (stepData.field === 'basic') {
      stepSpecificInstructions = `
当前步骤是第一步：基础信息
这是用户第一次提供想法，需要从用户输入中提取或生成：
- name: 世界名称（如果用户提到了名称，提取；如果没有，基于想法生成一个简洁有力的名称，4-8个字）
- description: 世界描述（基于用户想法，生成50-100字的核心描述，包含时代背景、主要特征、核心冲突）
- summary: 世界概要（基于用户想法，生成100-150字的详细概要，包含独特之处、社会结构、主要矛盾）
- preText: 世界观设定（基于用户想法，生成600-800字的完整世界观，包含历史背景、社会结构、核心冲突、独特设定、氛围描述）

如果用户只提供了简单的想法描述，需要基于这个想法进行优化扩写，生成完整的基础信息。
如果用户已经提供了较详细的信息，则提取并整理。
`
    }
    
const context = `${personalityPrefix}

你是一个世界创建引导员，按照固定流程引导用户创建世界。

当前步骤：${stepData.title}（${stepData.description}）
${nextStepHint}
${stepSpecificInstructions}

已收集的世界信息：
${JSON.stringify(worldData, null, 2)}

用户输入：${userInput}

你的任务：
1. 提取用户输入中与当前步骤相关的信息，保存到extractedData中
2. 如果是第一步且用户提供了初始想法，基于想法进行优化扩写，生成完整的基础信息（name、description、summary、preText）
3. 用引导员的说话方式简短确认用户的输入
4. 如果当前步骤已完成，引导用户进入下一步；如果未完成，继续引导完成当前步骤

重要规则：
- 如果已有数据中某个字段已经有内容（如name、description、preText等），不要覆盖它
- 只在字段为空时才提取或生成新内容
- 如果用户输入的内容与已有数据冲突，优先保留已有数据，除非用户明确要求修改
- 按照固定流程引导：基础信息 → 世界观设定 → 系统规则 → 地区设定 → 角色设定
- 不要跳过步骤，按顺序引导
- 第一步时，如果用户只提供了简单想法，需要基于想法进行优化扩写，生成完整的基础框架
- extractedData中的所有字段必须是第三人称的纯设定内容，禁止出现引导员的口吻、emoji、对话或第一人称表达

回复格式要求：
- 用引导员的说话方式，简短确认，1-2句话即可
- 段落间不要加空行，使用自然流畅的语气
- 不要使用markdown标记（**、##、-等）
- 不要在message中列出选项内容
- 不要追问"你觉得XXX怎么样"、"还有什么想法"等细节问题
- 如果需要修改，在message中提示用户"可以直接在输入框告诉我，比如「改名为 XXX」"

快捷回复选项（重要）：
- 只提供一个选项："继续"
- 如果用户想修改或添加内容，提示他们直接在输入框输入

返回JSON格式：
{
  "message": "简短的确认回复（1-2句话，用引导员的说话方式。如需修改，提示用户直接输入）",
  "extractedData": {
    ${stepData.field === 'basic' ? `"name": "世界名称（如果用户提到了名称，提取；如果没有，基于想法生成）",
    "description": "世界描述（50-100字，基于用户想法优化扩写）",
    "summary": "世界概要（100-150字，基于用户想法优化扩写）",
    "preText": "世界观设定（600-800字，基于用户想法优化扩写，包含历史背景、社会结构、核心冲突、独特设定、氛围描述）"` : '// 提取的结构化数据'}
  },
  "stepComplete": false,
  "quickReplies": ["继续"]
}
`
    return context
  }

  // 构建角色信息提取提示词
  const buildCharacterExtractionPrompt = (userInput) => {
    // 获取个性化Prompt前缀
    const personalityPrefix = aiPersonalityService.getPersonalityPromptPrefix()
    
    return `${personalityPrefix}

你是一个世界创建引导员，正在引导用户完成角色设定步骤。

当前步骤：角色设定（重要NPC和角色）
这是最后一个步骤，完成后可以发布世界。

用户输入：
${userInput}

已有世界信息：
${JSON.stringify(worldData, null, 2)}

你的任务：
1. 提取用户输入的角色信息
2. 用引导员的说话方式简短确认

提取以下信息（用户没提到的就留空，不要追问）：
1. 名字 (name)
2. 职业/身份 (role)
3. 描述 (description) - 综合用户提供的所有信息

如果用户一次输入了多个角色，请分别提取。

回复要求：
- 用引导员的说话方式，简短确认，1-2句话
- 不要追问细节，不要评估完整度
- 不要使用markdown标记（**、##、-等）
- 如果角色设定完成，可以提示用户可以发布世界了

返回JSON格式：
{
  "message": "简短确认（1-2句话，用引导员的说话方式）",
  "extractedData": {
    "characters": [
      {
        "name": "角色名字",
        "role": "职业/身份",
        "description": "用户提供的描述（原文整理）",
        "avatar": ""
      }
    ]
  },
  "needsRefinement": false,
  "stepComplete": false,
  "quickReplies": ["继续"]
}

重要：
- 不要评估完整度，needsRefinement永远为false
- 不要建议细化，不要追问细节
- 只提供"继续"选项
`
  }

  // 解析AI响应
  const parseAIResponse = (response) => {
    try {
      // 尝试从响应中提取JSON
      const jsonMatch = response.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0])
        
        // 清理message格式
        if (parsed.message) {
          parsed.message = cleanMessageFormat(parsed.message)
        }
        
        return parsed
      }
      // 如果没有JSON，返回基本响应
      return {
        message: cleanMessageFormat(response),
        extractedData: {},
        stepComplete: false,
        quickReplies: []
      }
    } catch (error) {
      return {
        message: cleanMessageFormat(response),
        extractedData: {},
        stepComplete: false,
        quickReplies: []
      }
    }
  }

  // 清理消息格式
  const cleanMessageFormat = (text) => {
    if (!text) return text
    
    // 移除markdown标记
    let cleaned = text
      .replace(/\*\*(.*?)\*\*/g, '$1') // 移除 **粗体**
      .replace(/##\s+(.*?)$/gm, '$1')  // 移除 ## 标题
      .replace(/^[-•]\s+/gm, '')       // 移除列表标记 - •
      .replace(/\n{3,}/g, '\n')        // 连续3个以上换行符替换为1个
    
    // 统一选项格式（如果AI在文字中包含了选项列表）
    cleaned = cleaned.replace(/[-•]\s*选项\d+[:：]/g, '') // 移除 "- 选项1:" 这样的格式
    
    return cleaned.trim()
  }

  // 触发字段高亮动画
  const triggerFieldHighlight = (fieldName) => {
    setHighlightedFields(prev => [...prev, fieldName])
    setUpdatedFields(prev => new Set([...prev, fieldName]))
    
    // 2秒后移除高亮
    setTimeout(() => {
      setHighlightedFields(prev => prev.filter(f => f !== fieldName))
    }, 2000)
  }

  // 保存到历史记录
  const saveToHistory = (newData) => {
    // 如果不是在最新的历史状态，移除后面的历史
    const newHistory = history.slice(0, historyIndex + 1)
    // 添加新状态（限制历史记录最多50条）
    const updatedHistory = [...newHistory, JSON.parse(JSON.stringify(newData))].slice(-50)
    setHistory(updatedHistory)
    setHistoryIndex(updatedHistory.length - 1)
  }

  // 撤销
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      setWorldData(JSON.parse(JSON.stringify(history[newIndex])))
      toast.success('已撤销')
    }
  }

  // 重做
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1
      setHistoryIndex(newIndex)
      setWorldData(JSON.parse(JSON.stringify(history[newIndex])))
      toast.success('已重做')
    }
  }

  // 保存字段编辑
  const handleSaveFieldEdit = (fieldType, value) => {
    // 对于字符串类型，检查是否为空
    if (typeof value === 'string' && (!value || !value.trim())) return
    // 对于对象类型（region/character），检查是否有内容
    if (typeof value === 'object' && value !== null) {
      if (fieldType === 'region' && (!value.name || !value.name.trim())) return
      if (fieldType === 'character' && (!value.name || !value.name.trim())) return
    }
    
    setWorldData(prev => {
      const updated = { ...prev }
      
      switch (fieldType) {
        case 'name':
          updated.name = value.trim()
          triggerFieldHighlight('name')
          break
        case 'description':
          updated.description = value.trim()
          triggerFieldHighlight('description')
          break
        case 'summary':
          updated.summary = value.trim()
          triggerFieldHighlight('summary')
          break
        case 'preText':
          updated.preText = value.trim()
          triggerFieldHighlight('preText')
          break
        case 'region':
          const regionIndex = editingRegionIndex
          if (regionIndex === -1) {
            // 添加新地区
            updated.regions = [...(prev.regions || []), value]
            triggerFieldHighlight('regions')
          } else if (regionIndex !== null && prev.regions && prev.regions[regionIndex]) {
            // 更新现有地区
            updated.regions = [...prev.regions]
            updated.regions[regionIndex] = { ...updated.regions[regionIndex], ...value }
            triggerFieldHighlight('regions')
          }
          break
        case 'character':
          const normalizedCharacter = normalizeCharacterData(value)
          const charIndex = editingCharacterIndex
          if (charIndex === -1) {
            // 添加新角色
            updated.characters = [...(prev.characters || []), {
              ...normalizedCharacter,
              completeness: calculateCharacterCompleteness(normalizedCharacter)
            }]
            triggerFieldHighlight('characters')
          } else if (charIndex !== null && prev.characters && prev.characters[charIndex]) {
            // 更新现有角色
            updated.characters = [...prev.characters]
            updated.characters[charIndex] = { 
              ...updated.characters[charIndex], 
              ...normalizedCharacter,
              completeness: calculateCharacterCompleteness({ ...updated.characters[charIndex], ...normalizedCharacter })
            }
            triggerFieldHighlight('characters')
          }
          break
        case 'quantitativeSystem':
          const qsIndex = editingQuantitativeSystemIndex
          if (qsIndex === -1) {
            // 添加新量化系统
            updated.quantitativeSystems = [...(prev.quantitativeSystems || []), value]
            triggerFieldHighlight('systems')
          } else if (qsIndex !== null && prev.quantitativeSystems && prev.quantitativeSystems[qsIndex]) {
            // 更新现有量化系统
            updated.quantitativeSystems = [...prev.quantitativeSystems]
            updated.quantitativeSystems[qsIndex] = { ...updated.quantitativeSystems[qsIndex], ...value }
            triggerFieldHighlight('systems')
          }
          break
        case 'system':
          if (editingSystemKey === '__new__') {
            // 添加新系统
            if (value && value.trim()) {
              updated.systems = { ...(prev.systems || {}), [value.trim()]: true }
              triggerFieldHighlight('systems')
            }
          } else if (editingSystemKey && prev.systems) {
            updated.systems = { ...prev.systems }
            if (value === null || value === '') {
              // 删除系统
              delete updated.systems[editingSystemKey]
            } else {
              // 更新系统值
              updated.systems[editingSystemKey] = value
            }
            triggerFieldHighlight('systems')
          }
          break
        case 'rule':
          const ruleIndex = editingRuleIndex
          if (ruleIndex === -1) {
            // 添加新规则
            updated.rules = [...(prev.rules || []), value]
            triggerFieldHighlight('systems')
          } else if (ruleIndex !== null && prev.rules && prev.rules[ruleIndex]) {
            // 更新现有规则
            updated.rules = [...prev.rules]
            updated.rules[ruleIndex] = { ...updated.rules[ruleIndex], ...value }
            triggerFieldHighlight('systems')
          }
          break
      }
      
      saveToHistory(prev)
      return updated
    })
    
    setEditingField(null)
    setEditingRegionIndex(null)
    setEditingCharacterIndex(null)
    setEditingQuantitativeSystemIndex(null)
    setEditingSystemKey(null)
    setEditingRuleIndex(null)
    toast.success('已保存')
  }
  
  // 取消编辑
  const handleCancelEdit = () => {
    setEditingField(null)
    setEditingRegionIndex(null)
    setEditingCharacterIndex(null)
    setEditingQuantitativeSystemIndex(null)
    setEditingSystemKey(null)
    setEditingRuleIndex(null)
  }

  // 更新世界数据
  const updateWorldData = (field, data) => {
    setWorldData(prev => {
      // 保存当前状态到历史记录（在更新之前）
      if (historyIndex === -1 || JSON.stringify(prev) !== JSON.stringify(history[historyIndex])) {
        saveToHistory(prev)
      }
      
      const updated = { ...prev }
      
      if (field === 'basic') {
        // 基本信息 - 只在字段为空或用户明确要求时才更新，避免意外覆盖
        if (data.name && (!prev.name || prev.name.trim() === '')) {
          updated.name = data.name
          triggerFieldHighlight('name')
        }
        if (data.description && (!prev.description || prev.description.trim() === '')) {
          updated.description = data.description
          triggerFieldHighlight('description')
        }
        if (data.summary && (!prev.summary || prev.summary.trim() === '')) {
          updated.summary = data.summary
          triggerFieldHighlight('summary')
        }
        // 其他字段（如tags等）可以合并
        if (data.tags) {
          updated.tags = [...(prev.tags || []), ...(data.tags || [])].filter((v, i, a) => a.indexOf(v) === i)
        }
      } else if (field === 'worldview') {
        // 世界观设定 - 只在字段为空或用户明确要求时才更新，避免意外覆盖
        if (data.preText && (!prev.preText || prev.preText.trim() === '')) {
          updated.preText = data.preText
          triggerFieldHighlight('preText')
        } else {
          updated.preText = prev.preText
        }
        if (data.summary && (!prev.summary || prev.summary.trim() === '')) {
          updated.summary = data.summary
        } else {
          updated.summary = prev.summary
        }
      } else if (field === 'systems') {
        // 系统与规则（包含量化系统）
        updated.systems = { ...(prev.systems || {}), ...(data.systems || {}) }
        updated.postText = data.postText || prev.postText
        if (data.quantitativeSystems) {
          updated.quantitativeSystems = data.quantitativeSystems
          triggerFieldHighlight('systems')
        }
      } else if (field === 'regions') {
        // 地区设定
        if (data.regions) {
          updated.regions = data.regions
          triggerFieldHighlight('regions')
        }
        if (data.worldBook) {
          updated.worldBook = [...prev.worldBook, ...data.worldBook]
        }
      } else if (field === 'characters') {
        // 角色设定 - 智能合并角色数据
        if (Array.isArray(data.characters) && data.characters.length > 0) {
          const normalizedCharactersInput = data.characters
            .filter(char => char && typeof char === 'object')
            .map(normalizeCharacterData)
            .filter(Boolean)
          
          // 如果是新角色数组，合并到现有角色中
          const existingCharacters = prev.characters || []
          const newCharacters = normalizedCharactersInput.length > 0
            ? normalizedCharactersInput
            : data.characters.filter(char => char && typeof char === 'object')
          
          if (newCharacters.length === 0) {
            return prev
          }
          
          // 合并策略：如果名字相同则更新，否则添加
          const mergedCharacters = [...existingCharacters]
          
          newCharacters.forEach(newChar => {
            const existingIndex = mergedCharacters.findIndex(c => 
              c.name && newChar.name && c.name === newChar.name
            )
            
            if (existingIndex >= 0) {
              // 更新现有角色，合并信息
              const mergedData = {
                ...mergedCharacters[existingIndex],
                ...newChar,
                // 合并traits数组
                traits: [
                  ...(mergedCharacters[existingIndex].traits || []),
                  ...(newChar.traits || [])
                ].filter((v, i, a) => typeof v === 'string' && v.trim() && a.indexOf(v) === i).map(v => v.trim()),
                // 保留更详细的描述
                description: newChar.description || mergedCharacters[existingIndex].description,
                background: newChar.background || mergedCharacters[existingIndex].background,
                motivation: newChar.motivation || mergedCharacters[existingIndex].motivation,
                relationships: newChar.relationships || mergedCharacters[existingIndex].relationships
              }
              const normalizedMerged = normalizeCharacterData(mergedData)
              mergedCharacters[existingIndex] = {
                ...normalizedMerged,
                // 更新完整度
                completeness: calculateCharacterCompleteness(normalizedMerged)
              }
            } else {
              // 添加新角色
              const normalizedNewChar = normalizeCharacterData(newChar)
              mergedCharacters.push({
                ...normalizedNewChar,
                completeness: calculateCharacterCompleteness(normalizedNewChar)
              })
            }
          })
          
          updated.characters = mergedCharacters
          triggerFieldHighlight('characters')
        }
      } else {
        // 其他字段直接合并，但保护基础字段不被覆盖
        Object.keys(data).forEach(key => {
          // 保护基础字段：如果已有内容，不覆盖
          if (['name', 'description', 'summary', 'preText'].includes(key)) {
            if (!prev[key] || prev[key].trim() === '') {
              updated[key] = data[key]
            }
          } else {
            updated[key] = data[key]
          }
        })
      }
      
      return updated
    })
  }

  // 根据引导员性格生成不同的示例
  const getExamples = () => {
    const personality = aiPersonalityService.getCurrentPersonality()
    switch (personality.id) {
      case 'royal_sister':
        return [
          '一个末代王朝的暗流涌动，权谋与野心交织',
          '魔法学院里的竞争与背叛，只有强者才能生存',
          '赛博朋克都市，科技巨头控制着每个人的命运'
        ]
      case 'sweet_girl':
      case 'warm_girl':
        return [
          '一个充满温情的小镇，每个人都有自己的故事',
          '魔法与日常交织的温馨世界',
          '未来城市里，人们用科技守护着最珍贵的情感'
        ]
      case 'reliable_big_brother':
        return [
          '战乱时代，英雄们守护家园的热血故事',
          '冒险者公会，伙伴们并肩作战的奇幻世界',
          '星际殖民时代，开拓者们建立新家园'
        ]
      case 'tech_master':
        return [
          '后启示录世界，用科技重建文明',
          '魔法科学化的新时代，理论与实践结合',
          '虚拟现实与真实世界的边界模糊'
        ]
      case 'intellectual':
        return [
          '基于历史逻辑构建的架空王朝，权力结构与文化体系完整',
          '遵循科学规律的魔法世界，每个设定都有其内在逻辑',
          '社会制度与科技发展相互影响的后现代都市'
        ]
      case 'gentleman':
        return [
          '一个充满深度与层次的世界，每个细节都经过精心设计',
          '优雅而复杂的世界观，人物关系与情节发展相互交织',
          '具有文学价值的世界设定，值得深入探索'
        ]
      case 'humorous':
        return [
          '充满烟火气的现代都市，各种有趣的人和事',
          '轻松幽默的奇幻世界，冒险与搞笑并存',
          '接地气的未来世界，科技与生活完美融合'
        ]
      case 'gentle':
        return [
          '一个充满温暖与希望的世界，每个人都有自己的故事',
          '细腻而感人的世界设定，情感与成长是主题',
          '温柔治愈的世界，让人感受到生活的美好'
        ]
      default:
        return [
          '乱世中崛起的传奇故事',
          '充满奇幻冒险的魔法世界',
          '科技高度发达的未来都市'
        ]
    }
  }

  // 根据引导员角色生成符合其特点的开场白
  const generateOpeningMessage = (personality) => {
    const examples = getExamples()
    const exampleText = examples.map((ex, i) => `${i + 1}. ${ex}`).join('\n')
    
    // 根据不同的引导员角色生成不同的开场白
    switch (personality.id) {
      case 'royal_sister': // 苏婉 - 成熟御姐，带点小傲娇
        return `${personality.avatar} ${personality.greeting}

告诉我，你想创建一个什么样的世界？别告诉我你还没想好，我可是很挑剔的。

比如说：
${exampleText}

直接在输入框告诉我你的想法，我会帮你优化扩展成完整的世界设定。`

      case 'sweet_girl': // 小糖 - 元气少女，真诚热情
        return `${personality.avatar} ${personality.greeting}

你想创建一个什么样的世界呢？我已经迫不及待想听你的想法了~

比如说：
${exampleText}

直接在输入框告诉我你的想法，我会帮你优化扩展成完整的世界设定！`

      case 'intellectual': // 顾言 - 理性学者，温和细致
        return `${personality.avatar} ${personality.greeting}

创建世界需要严谨的逻辑和丰富的想象力，我们一起来完成它。

请告诉我，你想创建一个什么样的世界？我会从逻辑和结构的角度帮你完善它。

比如说：
${exampleText}

你可以直接在输入框描述你的想法，我会帮你优化扩展成完整的世界设定。`

      case 'gentleman': // 沈墨 - 温和绅士，专业可靠
        return `${personality.avatar} ${personality.greeting}

能协助您创建世界是我的荣幸，我会认真对待您的每一个想法。

请问，您想创建一个什么样的世界呢？我会用专业的方式帮您完善它。

比如说：
${exampleText}

您可以直接在输入框告诉我您的想法，我会帮您优化扩展成完整的世界设定。`

      case 'humorous': // 乐天 - 幽默随和，接地气
        return `${personality.avatar} ${personality.greeting}

创造世界这事儿听起来很酷，咱们一起把它搞出来！

你想创建一个什么样的世界？来，说说你的想法，咱们一起聊聊！

比如说：
${exampleText}

直接在输入框告诉我你的想法，我会帮你优化扩展成完整的世界设定！`

      case 'gentle': // 苏柔 - 温柔耐心，细腻体贴
        return `${personality.avatar} ${personality.greeting}

创造世界是一段美妙的旅程，不用着急，我会一直陪着你慢慢完成的。

你想创建一个什么样的世界呢？慢慢说，我会认真听的。

比如说：
${exampleText}

你可以直接在输入框告诉我你的想法，我会帮你优化扩展成完整的世界设定。`

      default:
        return `${personality.avatar} ${personality.greeting}

今天我们要一起创建一个完整的世界架构。

告诉我，你想创建一个什么样的世界？

比如说：
${exampleText}

直接在输入框告诉我你的想法，我会帮你优化扩展成完整的世界设定！`
    }
  }

  // 询问下一步问题
  const askNextStepQuestion = async () => {
    const nextStep = CREATION_STEPS.find(step => step.id === currentStep) || CREATION_STEPS[0]
    if (!nextStep) return

    const personality = aiPersonalityService.getCurrentPersonality()
    
    const questions = {
      1: generateOpeningMessage(personality),
      2: `${personality.avatar} 太好了！基础信息已经记录。现在来设定世界观。

请告诉我这个世界的核心设定和背景故事，比如：
• 时代背景
• 核心冲突
• 独特设定
• 社会结构

你可以直接在输入框描述你的想法。`,
      3: `${personality.avatar} 世界观设定完成！现在来添加系统规则。

请告诉我这个世界的规则、数值系统等，比如：
• 声望系统
• 资源管理
• 事件系统
• 战斗系统

你可以直接在输入框描述你的想法。`,
      4: `${personality.avatar} 系统规则设定完成！现在来创建世界的主要地区。

请告诉我这个世界的地区设定，比如：
• 地区名称
• 地理特征
• 主要势力
• 特色资源

你可以直接在输入框描述你的想法。`,
      5: `${personality.avatar} 地区设定完成！最后添加重要角色。

请告诉我这个世界的角色设定，比如：
• 角色名字
• 职业和身份
• 性格特点
• 背景故事

你可以直接在输入框描述你的想法。`,
      6: `${personality.avatar} 🎉 太棒了！世界创建完成！

我已经帮你创建了：
✓ ${worldData.name || '世界名称'}
✓ 完整的世界观设定
✓ ${Object.keys(worldData.systems || {}).filter(k => worldData.systems[k]).length}个系统规则
✓ ${worldData.regions?.length || 0}个地区
✓ ${worldData.characters?.length || 0}个角色

右侧可以看到完整内容。现在你可以发布到世界库或保存到我的游戏。`
    }

    const msg = {
      role: 'assistant',
      content: questions[nextStep.id] || '请继续...',
      timestamp: Date.now()
    }

    setMessages(prev => [...prev, msg])
    
    // 第一步（开场白）不提供快捷回复，等待用户输入想法
    // 其他步骤提供"继续"选项
    if ([1, 2, 3].includes(nextStep.id)) {
      setQuickReplies([])
    } else {
      setQuickReplies(['继续'])
    }
  }

  // 使用快捷回复
  const handleQuickReply = async (reply) => {
    // 处理"继续"选项（默认让AI完成）
    if (reply === '继续' || reply.includes('继续')) {
      // 检查上下文，如果最近有关于增加内容的对话，默认让AI完成
      const lastAssistantMsg = messages.filter(m => m.role === 'assistant').pop()
      if (lastAssistantMsg && lastAssistantMsg.content) {
        if (lastAssistantMsg.content.includes('我来帮你') || lastAssistantMsg.content.includes('你可以选择')) {
          // 如果最近有提供选项的消息，默认选择"我来帮你完成"
          let targetType = null
          let generateTemplate = ''
          
          if (lastAssistantMsg.content.includes('角色')) {
            targetType = 'character'
            generateTemplate = '生成1个角色'
          } else if (lastAssistantMsg.content.includes('地区')) {
            targetType = 'region'
            generateTemplate = '生成1个地区'
          } else if (lastAssistantMsg.content.includes('规则')) {
            targetType = 'rule'
            generateTemplate = '生成1条规则'
          }
          
          if (targetType) {
            const personality = currentPersonality
            setMessages(prev => [...prev, {
              role: 'user',
              content: '继续',
              timestamp: Date.now()
            }, {
              role: 'assistant',
              content: `${personality.avatar} 好的，让我来帮你完成...`,
              timestamp: Date.now()
            }])
            
            setIsAIThinking(true)
            setQuickReplies([])
            
            try {
              await generateCompleteContent(generateTemplate)
            } catch (error) {
              console.error('生成失败:', error)
              toast.error('生成失败，请重试')
              setIsAIThinking(false)
              const personality = currentPersonality
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `${personality.avatar} 抱歉，生成失败了。请直接在输入框告诉我具体内容。`,
                timestamp: Date.now()
              }])
              setQuickReplies([])
            }
            return
          }
        }
      }
      
      // 原有的"继续"处理逻辑
      // 检查当前步骤是否完成
      const stepData = CREATION_STEPS.find(s => s.id === currentStep)
      
      // 判断当前步骤是否完成
      let isStepComplete = false
      switch (stepData.field) {
        case 'basic':
          isStepComplete = !!(worldData.name && worldData.description)
          break
        case 'worldview':
          isStepComplete = !!(worldData.preText)
          break
        case 'systems':
          isStepComplete = !!(worldData.systems && Object.keys(worldData.systems).length > 0) || 
                          !!(worldData.quantitativeSystems && worldData.quantitativeSystems.length > 0) ||
                          !!(worldData.rules && worldData.rules.length > 0)
          break
        case 'regions':
          isStepComplete = !!(worldData.regions && worldData.regions.length > 0)
          break
        case 'characters':
          isStepComplete = !!(worldData.characters && worldData.characters.length > 0)
          break
        default:
          isStepComplete = true
      }
      
      // 显示用户选择
      setMessages(prev => [...prev, {
        role: 'user',
        content: '继续',
        timestamp: Date.now()
      }])
      
      // 如果当前步骤完成，进入下一步
      if (isStepComplete && currentStep < CREATION_STEPS.length) {
        const nextStep = CREATION_STEPS.find(s => s.id === currentStep + 1)
        if (nextStep) {
          // 进入下一步
          setCurrentStep(prev => prev + 1)
          
          // 让AI自动生成下一步的内容，而不是只显示问题
          const personality = currentPersonality
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 好的，让我来帮你完成${nextStep.title}...`,
            timestamp: Date.now()
          }])
          
          setIsAIThinking(true)
          
          // 根据下一步骤生成对应的内容
          let template = ''
          switch (nextStep.field) {
            case 'worldview':
              template = '生成世界观'
              break
            case 'systems':
              template = '生成系统规则'
              break
            case 'regions':
              template = '生成3个地区'
              break
            case 'characters':
              template = '生成3个角色'
              break
            default:
              // 如果没有匹配的模板，显示问题让用户输入
              askNextStepQuestion()
              setIsAIThinking(false)
              return
          }
          
          // 调用AI生成下一个环节的内容
          try {
            await generateCompleteContent(template)
          } catch (error) {
            console.error('生成失败:', error)
            toast.error('生成失败，请重试')
            setIsAIThinking(false)
            // 如果生成失败，显示问题让用户输入
            askNextStepQuestion()
          }
        }
      } else if (currentStep === CREATION_STEPS.length) {
        // 所有步骤完成，可以发布
        const personality = currentPersonality
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${personality.avatar} 太好了！世界创建完成！你可以发布到世界库或保存到我的游戏。`,
          timestamp: Date.now()
        }])
        setQuickReplies([])
      } else {
        // 当前步骤未完成
        // 如果是可以通过AI自动生成的步骤（地区、角色），自动生成
        // 如果是需要用户输入的步骤（基础信息、世界观、系统规则），显示问题让用户输入
        if (stepData.field === 'regions' || stepData.field === 'characters') {
          // 自动生成地区或角色
          const personality = currentPersonality
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 好的，让我来帮你完成${stepData.title}...`,
            timestamp: Date.now()
          }])
          
          setIsAIThinking(true)
          
          let template = ''
          if (stepData.field === 'regions') {
            template = '生成3个地区'
          } else if (stepData.field === 'characters') {
            template = '生成3个角色'
          }
          
          try {
            await generateCompleteContent(template)
          } catch (error) {
            console.error('生成失败:', error)
            toast.error('生成失败，请重试')
            setIsAIThinking(false)
            // 如果生成失败，显示问题让用户输入
            const personality = currentPersonality
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `${personality.avatar} 抱歉，生成失败了。请直接在输入框告诉我这个世界的${stepData.title}。`,
              timestamp: Date.now()
            }])
            setQuickReplies([])
          }
        } else {
          // 需要用户输入的步骤，显示问题
          const personality = currentPersonality
          let promptMessage = ''
          switch (stepData.field) {
            case 'basic':
              promptMessage = `${personality.avatar} 让我们先完成基础信息。请告诉我：\n\n• 你想要创建一个什么样的世界？\n• 给这个世界起个名字\n• 简单描述一下这个世界`
              break
            case 'worldview':
              promptMessage = `${personality.avatar} 现在来设定世界观。请告诉我这个世界的核心设定和背景故事。`
              break
            case 'systems':
              promptMessage = `${personality.avatar} 接下来设定系统规则。请告诉我这个世界的规则、数值系统等。`
              break
            default:
              promptMessage = '请继续...'
          }
          
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: promptMessage,
          timestamp: Date.now()
        }])
        const needsUserInput = ['basic', 'worldview', 'systems'].includes(stepData.field)
        setQuickReplies(needsUserInput ? [] : ['继续'])
        }
      }
    } else if (reply === '我来帮你完成' || reply === '你告诉我') {
      // 处理增加内容的快捷回复
      // 需要从上下文或最近的消息中识别要增加什么
      const lastAssistantMsg = messages.filter(m => m.role === 'assistant').pop()
      let targetType = null
      let generateTemplate = ''
      
      if (lastAssistantMsg && lastAssistantMsg.content) {
        if (lastAssistantMsg.content.includes('角色')) {
          targetType = 'character'
          generateTemplate = '生成1个角色'
        } else if (lastAssistantMsg.content.includes('地区')) {
          targetType = 'region'
          generateTemplate = '生成1个地区'
        } else if (lastAssistantMsg.content.includes('规则')) {
          targetType = 'rule'
          generateTemplate = '生成1条规则'
        }
      }
      
      if (reply === '我来帮你完成' && targetType) {
        // AI自动生成
        const personality = currentPersonality
        setMessages(prev => [...prev, {
          role: 'user',
          content: '我来帮你完成',
          timestamp: Date.now()
        }, {
          role: 'assistant',
          content: `${personality.avatar} 好的，让我来帮你完成...`,
          timestamp: Date.now()
        }])
        
        setIsAIThinking(true)
        setQuickReplies([])
        
        try {
          await generateCompleteContent(generateTemplate)
        } catch (error) {
          console.error('生成失败:', error)
          toast.error('生成失败，请重试')
          setIsAIThinking(false)
          const personality = currentPersonality
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 抱歉，生成失败了。请直接在输入框告诉我具体内容。`,
            timestamp: Date.now()
          }])
          setQuickReplies([])
        }
      } else if (reply === '你告诉我') {
        // 让用户输入
        const personality = currentPersonality
        setMessages(prev => [...prev, {
          role: 'user',
          content: '你告诉我',
          timestamp: Date.now()
        }, {
          role: 'assistant',
          content: `${personality.avatar} 好的，请直接在输入框告诉我${targetType === 'character' ? '角色的名字、职业和描述' : targetType === 'region' ? '地区的名称、类型和描述' : '规则的名称和详细说明'}。`,
          timestamp: Date.now()
        }])
        setQuickReplies([])
        setIsAIThinking(false)
      } else {
        // 其他回复作为用户输入处理
        setMessages(prev => [...prev, {
          role: 'user',
          content: reply,
          timestamp: Date.now()
        }])
        setQuickReplies([])
        await processUserInput(reply)
      }
    } else {
      // 其他回复作为用户输入处理
      setMessages(prev => [...prev, {
        role: 'user',
        content: reply,
        timestamp: Date.now()
      }])
      setQuickReplies([])
      await processUserInput(reply)
    }
  }

  // 生成完整内容（快速模板）
  const generateCompleteContent = async (template) => {
    setIsAIThinking(true)
    setQuickReplies([])
    
    try {
      const personality = aiPersonalityService.getCurrentPersonality()
      let prompt = ''
      
      // 判断是否为用户的初始想法（不包含特定关键词）
      const isUserIdea = !template.includes('世界观') && !template.includes('系统') && 
                         !template.includes('地区') && !template.includes('角色')
      
      if (isUserIdea) {
        // 用户输入的是初始想法，生成完整的基础框架
        prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

用户的世界想法：「${template}」

请基于这个想法，生成一个完整的世界基础框架，必须以JSON格式返回（所有字段必须为客观第三人称设定描述，禁止出现引导员口吻、说明、对话或emoji）：

{
  "name": "一个简洁有力的世界名称（4-8个字）",
  "description": "世界的核心描述（50-100字，包含时代背景、主要特征、核心冲突）",
  "summary": "世界概要（100-150字，更详细的世界介绍，包含世界的独特之处、社会结构、主要矛盾）",
  "preText": "完整的世界观设定（600-800字，包含：历史背景、社会结构、核心冲突、独特设定、氛围描述，要有画面感和代入感）"
}

要求：
1. 名称简洁有力，符合世界风格
2. 描述和概要层次分明，逐步深入
3. 世界观要丰富详实，有画面感
4. 所有内容都必须是客观第三人称的设定描述，禁止出现引导员口吻、emoji或第一人称
5. 直接返回JSON，不要其他内容

开始生成：`

        const response = await RealAIService.callLanguageModel(prompt)
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        
        if (jsonMatch) {
          const basicInfo = JSON.parse(jsonMatch[0])
          
          // 只在字段为空时才覆盖，避免覆盖用户已有内容
          setWorldData(prev => ({
            ...prev,
            name: prev.name && prev.name.trim() ? prev.name : basicInfo.name,
            description: prev.description && prev.description.trim() ? prev.description : basicInfo.description,
            summary: prev.summary && prev.summary.trim() ? prev.summary : basicInfo.summary,
            preText: prev.preText && prev.preText.trim() ? prev.preText : basicInfo.preText
          }))
          
          // 高亮所有更新的字段
          triggerFieldHighlight('name')
          triggerFieldHighlight('description')
          triggerFieldHighlight('summary')
          triggerFieldHighlight('preText')
          saveToHistory(worldData)
          
          const personality = aiPersonalityService.getCurrentPersonality()
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 太好了！我已经为你生成了完整的基础框架：\n\n📝 世界名称：${basicInfo.name}\n✨ 世界描述：${basicInfo.description}\n\n完整的世界观和概要已保存到右侧，你可以查看详情。\n\n💡 如果需要修改基础信息，直接在输入框告诉我即可，比如「改名为 XXX」或「修改描述：XXX」`,
            timestamp: Date.now()
          }])
          
          // 设置"继续"选项
          setQuickReplies(['继续'])
          
          // 自动进入下一步（世界观设定）
          setTimeout(() => {
            setCurrentStep(2)
          }, 1500)
        }
        
      } else if (template.includes('世界观')) {
        const theme = template.replace(/世界观$/, '')
        prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

基于已有的世界基础信息，请生成800-1200字的完整世界观设定。只输出世界观正文，禁止任何问候、说明、emoji或引导语。

世界信息：
- 名称：${worldData.name || '未命名'}
- 描述：${worldData.description || '暂无'}
- 概要：${worldData.summary || '暂无'}

要求：
1. 包含：时代背景、核心冲突、独特设定、社会结构
2. 基于已有的世界信息进行扩展和深化
3. 内容必须为第三人称的客观设定描述，禁止出现引导员口吻、第一人称、说明、emoji或对话
4. 语言生动，有画面感
5. 直接给出内容，不要开场白或附加说明

开始生成：`

        const response = await RealAIService.callLanguageModel(prompt)
        const rawContent = response.trim()
        const personality = aiPersonalityService.getCurrentPersonality()
        const content = sanitizeGeneratedContent(rawContent, personality)
        
        // 只在世界观为空时才覆盖，避免覆盖用户已有内容
        setWorldData(prev => ({
          ...prev,
          preText: prev.preText && prev.preText.trim() ? prev.preText : content
        }))
        
        triggerFieldHighlight('worldview')
        saveToHistory(worldData)
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${personality.avatar} 世界观已生成！\n\n${content.substring(0, 200)}...\n\n━━━━━━━━━━\n✅ 完整内容已保存到右侧\n\n如果内容满意，可以点击"继续"进入下一步（系统规则）。如果需要修改，直接在输入框告诉我即可。`,
          timestamp: Date.now()
        }])
        
        // 设置"继续"选项
        setQuickReplies(['继续'])
        
        // 不自动进入下一步，等待用户点击"继续"
        // 这样用户可以查看生成的内容并决定是否修改
        
      } else if (template.includes('系统')) {
        prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

世界信息：
- 名称：${worldData.name}
- 描述：${worldData.description}
- 世界观：${worldData.preText?.substring(0, 200)}...

请为这个世界生成合适的系统规则和机制，必须以JSON格式返回（字段内容必须为第三人称设定描述，禁止出现引导员口吻、说明、对话或emoji）：

{
  "systems": {
    "reputation": true,
    "resources": true,
    "combat": false,
    "social": true,
    "economic": true
  },
  "rules": [
    {
      "name": "规则名称",
      "description": "详细说明这个规则如何影响世界（50-100字）"
    }
  ]
}

要求：
1. 根据世界的风格选择合适的系统（声望、资源、战斗、社交、经济等）
2. 至少生成3-5条核心规则
3. 规则要符合世界观，有实际游戏意义
4. 描述要详细，让玩家理解规则的作用
5. 直接返回JSON，不要其他内容

开始生成：`

        const response = await RealAIService.callLanguageModel(prompt)
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        
        if (jsonMatch) {
          const data = JSON.parse(jsonMatch[0])
          setWorldData(prev => ({
            ...prev,
            systems: data.systems || {},
            rules: data.rules || []
          }))
          
          triggerFieldHighlight('systems')
          saveToHistory(worldData)
          
          const ruleCount = data.rules?.length || 0
          const enabledSystems = Object.keys(data.systems || {}).filter(k => data.systems[k])
          const systemCount = enabledSystems.length
          
          // 系统说明映射
          const systemNames = {
            'reputation': '声望系统',
            'resources': '资源管理',
            'combat': '战斗系统',
            'social': '社交系统',
            'economic': '经济系统',
            'magic': '魔法系统',
            'technology': '科技系统',
            'politics': '政治系统'
          }
          
          const systemList = enabledSystems.map(s => systemNames[s] || s).join('、')
          
          const personality = aiPersonalityService.getCurrentPersonality()
          
          // 构建详细的消息，解释系统和规则的用途
          let messageContent = `${personality.avatar} 系统规则已生成！\n\n`
          messageContent += `📊 已配置 ${systemCount} 个系统：${systemList}\n`
          messageContent += `📜 已创建 ${ruleCount} 条核心规则\n\n`
          messageContent += `💡 系统规则说明：\n`
          messageContent += `• 系统决定了这个世界运行的核心机制（如声望、资源、战斗等）\n`
          messageContent += `• 规则定义了这些系统如何具体运作，影响玩家的体验和选择\n`
          messageContent += `• 这些设定将用于世界模拟器，让AI能够根据规则来模拟世界的发展\n\n`
          messageContent += `完整内容已保存到右侧，你可以查看详情。\n\n`
          messageContent += `如果内容满意，可以点击"继续"进入下一步（地区设定）。如果需要修改，直接在输入框告诉我即可。`
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: messageContent,
            timestamp: Date.now()
          }])
          
          // 设置"继续"选项
          setQuickReplies(['继续'])
          
          // 更新步骤到4（地区设定），但不自动进入下一步
          // 等待用户点击"继续"后再自动生成地区
          setCurrentStep(4)
        }
        
      } else if (template.includes('地区')) {
        const count = template.includes('5个') ? 5 : (template.includes('4个') ? 4 : 3)
        prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

世界信息：
- 名称：${worldData.name}
- 描述：${worldData.description}
- 世界观：${worldData.preText?.substring(0, 200)}...

请为这个世界生成${count}个有特色的地区，必须以JSON数组格式返回（字段内容必须为第三人称设定描述，禁止出现引导员口吻、说明、对话或emoji）：

[
  {
    "name": "地区名称",
    "description": "详细描述地区的特点、历史、氛围、主要建筑或地标（100-150字）",
    "type": "城市|村落|秘境|遗迹|自然"
  }
]

要求：
1. 地区要符合世界观，各具特色
2. 包含不同类型的地区（城市、村落、秘境等）
3. 描述要有画面感，让人能想象出地区的样子
4. 每个地区都应该有独特的作用和故事
5. 直接返回JSON数组，不要其他内容

开始生成：`

        const response = await RealAIService.callLanguageModel(prompt)
        const jsonMatch = response.match(/\[[\s\S]*\]/)
        
        if (jsonMatch) {
          const regions = JSON.parse(jsonMatch[0])
          setWorldData(prev => ({
            ...prev,
            regions
          }))
          
          triggerFieldHighlight('regions')
          saveToHistory(worldData)
          
          const personality = aiPersonalityService.getCurrentPersonality()
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 已生成${count}个特色地区！\n\n${regions.map(r => `• ${r.name}（${r.type}）`).join('\n')}\n\n完整描述已保存到右侧\n\n如果内容满意，可以点击"继续"进入下一步（角色设定）。如果需要修改，直接在输入框告诉我即可。`,
            timestamp: Date.now()
          }])
          
          // 设置"继续"选项
          setQuickReplies(['继续'])
          
          // 更新步骤到5（角色设定），但不自动进入下一步
          // 等待用户点击"继续"后再自动生成角色
          setCurrentStep(5)
        }
        
      } else if (template.includes('角色')) {
        const count = template.includes('5个') ? 5 : (template.includes('4个') ? 4 : 3)
        prompt = `${aiPersonalityService.getPersonalityPromptPrefix()}

世界信息：
- 名称：${worldData.name}
- 描述：${worldData.description}
- 世界观：${worldData.preText?.substring(0, 200)}...
- 地区：${worldData.regions?.map(r => r.name).join('、') || '未设置'}

请为这个世界生成${count}个核心角色，必须以JSON数组格式返回（字段内容必须为第三人称设定描述，禁止出现引导员口吻、说明、对话或emoji）：

[
  {
    "name": "角色姓名",
    "role": "职业/身份",
    "description": "详细描述角色的性格、背景故事、外貌特征、目标动机（120-180字）",
    "avatar": ""
  }
]

要求：
1. 角色要符合世界观，各具特色
2. 包含不同身份和立场的角色（统治者、商人、英雄、反派等）
3. 性格和背景要丰富立体，有冲突和故事性
4. 每个角色都应该有自己的动机和目标
5. 如果有地区信息，可以为角色分配所在地区
6. 直接返回JSON数组，不要其他内容

开始生成：`

        const response = await RealAIService.callLanguageModel(prompt)
        const jsonMatch = response.match(/\[[\s\S]*\]/)
        
        if (jsonMatch) {
          const characters = JSON.parse(jsonMatch[0])
          setWorldData(prev => ({
            ...prev,
            characters
          }))
          
          triggerFieldHighlight('characters')
          saveToHistory(worldData)
          
          const personality = aiPersonalityService.getCurrentPersonality()
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `${personality.avatar} 已生成${count}个核心角色！\n\n${characters.map(c => `• ${c.name}（${c.role}）`).join('\n')}\n\n完整信息已保存到右侧。\n\n🎉 恭喜！世界的基础框架已经完成了！`,
            timestamp: Date.now()
          }])
          
          // 设置"继续"选项
          setQuickReplies(['继续'])
          
          setTimeout(() => {
            setCurrentStep(6)
          }, 1000)
        }
      }
      
    } catch (error) {
      console.error('生成失败:', error)
      toast.error('生成失败')
    } finally {
      setIsAIThinking(false)
    }
  }

  // 处理角色细化
  const handleCharacterRefinement = async (characterName) => {
    setIsAIThinking(true)
    
    // 添加用户消息
    setMessages(prev => [...prev, {
      role: 'user',
      content: `我想细化"${characterName}"这个角色`,
      timestamp: Date.now()
    }])
    
    try {
      // 查找对应的角色
      const character = worldData.characters.find(c => c.name === characterName) ||
                       worldData.characters[parseInt(characterName.replace('角色', '')) - 1]
      
      if (!character) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '抱歉，没有找到这个角色。请重新选择。',
          timestamp: Date.now()
        }])
        setIsAIThinking(false)
        return
      }
      
      // 构建细化提示词
      const prompt = `你是角色设定专家。用户想要细化以下角色：

角色信息：
${JSON.stringify(character, null, 2)}

当前角色完整度：${character.completeness?.percentage || 0}%

请分析缺失的信息，并以友好的方式询问用户，帮助补充以下内容：
${!character.completeness?.hasName ? '- 角色的名字\n' : ''}
${!character.completeness?.hasRole ? '- 角色的职业/身份\n' : ''}
${!character.completeness?.hasBackground ? '- 角色的背景故事（出身、经历等）\n' : ''}
${!character.completeness?.hasTraits ? '- 角色的特点（性格、外貌、能力等）\n' : ''}
${!character.completeness?.hasMotivation ? '- 角色的目标和动机\n' : ''}

请提出2-3个具体的引导性问题，帮助用户补充这些信息。

返回JSON格式：
{
  "message": "友好的询问消息，引导用户补充信息",
  "questions": ["问题1", "问题2", "问题3"],
  "quickReplies": ["快捷回复1", "快捷回复2", "快捷回复3"]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      const parsed = parseAIResponse(response)
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: parsed.message,
        timestamp: Date.now()
      }])
      
      if (parsed.quickReplies) {
        setQuickReplies(parsed.quickReplies)
      }
      
    } catch (error) {
      console.error('细化失败:', error)
      toast.error('细化失败，请重试')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '抱歉，细化遇到了问题。请直接描述你想补充的内容。',
        timestamp: Date.now()
      }])
    } finally {
      setIsAIThinking(false)
    }
  }

  // 完成创建
  const handleFinishCreation = async (isPublic) => {
    try {
      const worldToSave = {
        ...worldData,
        isPublic,
        status: isPublic ? 'published' : 'draft',
        creatorId: 'default-user',
        createdAt: new Date().toISOString()
      }

      await worldSimulatorStorageService.saveWorld(worldToSave)
      await loadWorlds()
      
      // 清除草稿
      localStorage.removeItem('world-creation-draft')
      localStorage.removeItem('world-creation-messages')
      localStorage.removeItem('world-creation-step')
      
      toast.success(isPublic ? '世界已发布到世界库！' : '世界已保存！')
      
      if (onComplete) {
        onComplete()
      } else {
        navigate('/world-simulator')
      }
    } catch (error) {
      console.error('保存世界失败:', error)
      toast.error('保存失败')
    }
  }

  // 导出世界
  const handleExport = () => {
    try {
      const json = worldTemplateService.exportToJSON(worldData)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${worldData.name || '我的世界'}-${Date.now()}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('导出成功！')
    } catch (error) {
      console.error('导出失败:', error)
      toast.error('导出失败')
    }
  }

  // 一键创建
  const handleAutoCreate = async () => {
    if (isAutoCreating) return
    
    setIsAutoCreating(true)
    setIsAIThinking(true)
    
    try {
      // 添加系统消息
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '🚀 好的！我将自动帮你完成剩余的所有步骤，请稍等...',
        timestamp: Date.now()
      }])

      // 基于当前已有信息，生成完整世界
      const prompt = `你是一个专业的世界创建助手。请基于以下已有信息，创建一个完整的世界模拟器设定：

当前世界信息：
${JSON.stringify(worldData, null, 2)}

当前步骤：${currentStep}/${CREATION_STEPS.length}

请自动补全所有缺失的信息：
1. 如果缺少基本信息，创建合适的名称、描述和概要
2. 补充完整的世界观设定（preText）
3. 设计系统与规则（包含量化系统）
4. 创建3-5个主要地区
5. 设计3-5个重要角色

返回完整的JSON格式世界数据：
{
  "name": "世界名称",
  "description": "世界描述",
  "summary": "世界概要",
  "preText": "详细的世界观设定",
  "postText": "AI核心指令集",
  "systems": {},
  "quantitativeSystems": [{"name": "系统名", "initial": 100}],
  "regions": [{"name": "地区名", "description": "描述"}],
  "characters": [{"name": "角色名", "role": "职业", "description": "描述"}]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      // 解析AI响应
      const jsonMatch = response.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const generatedData = JSON.parse(jsonMatch[0])
        
        // 合并数据
        setWorldData(prev => ({
          ...prev,
          ...generatedData,
          // 保留用户已经输入的数据
          name: prev.name || generatedData.name,
          description: prev.description || generatedData.description
        }))
        
        // 跳到最后一步
        setCurrentStep(CREATION_STEPS.length)
        
        // 添加完成消息
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '✨ 完成！我已经自动补全了所有世界设定。\n\n请在右侧查看完整的世界文档，确认无误后即可发布或保存。',
          timestamp: Date.now()
        }])
        
        setQuickReplies(['发布到世界库', '保存到我的游戏', '继续编辑'])
        
        toast.success('世界创建完成！')
      } else {
        throw new Error('AI返回格式错误')
      }
      
    } catch (error) {
      console.error('一键创建失败:', error)
      toast.error('自动创建失败，请手动继续')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '抱歉，自动创建遇到了问题。让我们继续手动完成吧。',
        timestamp: Date.now()
      }])
    } finally {
      setIsAutoCreating(false)
      setIsAIThinking(false)
    }
  }

  // 处理文件导入
  const handleFileImport = async (file) => {
    setIsAIThinking(true)
    setShowImportProgress(true)
    setImportProgress({ percentage: 0, message: '正在读取文件...', steps: [] })
    
    try {
      let content = ''
      
      // 根据文件类型读取内容
      if (file.type === 'application/json') {
        content = await file.text()
        
        // 尝试直接解析JSON
        try {
          const importedData = JSON.parse(content)
          
          // 如果是标准格式，使用智能导入
          if (importedData.name || importedData.world_book) {
            console.log('🚀 开始智能导入，文件大小:', content.length)
            
            // 使用新的importFromJSON方法，带进度回调
            const result = worldTemplateService.importFromJSON(content, (progress) => {
              console.log('📊 导入进度:', progress.percentage + '%', progress.message)
              setImportProgress(progress)
              
              // 根据进度触发字段高亮
              if (progress.completedFields) {
                const fieldsArray = Array.from(progress.completedFields)
                fieldsArray.forEach(field => {
                  triggerFieldHighlight(field)
                })
              }
            })
            
            console.log('✅ 导入完成，数据:', result.data)
            console.log('📈 完整度:', result.completeness)
            
            // 设置导入的数据
            setWorldData(result.data)
            
            // 保存到历史记录（使用导入的新数据）
            saveToHistory(result.data)
            
            // 根据完整度信息更新UI
            const { completeness } = result
            const completedSteps = Object.entries(completeness.checks)
              .filter(([_, isComplete]) => isComplete)
              .map(([step]) => step)
            
            // 高亮已完成的步骤
            completedSteps.forEach(step => {
              triggerFieldHighlight(step)
            })
            
            // 设置当前步骤到第一个未完成的步骤，或最后一步
            if (completeness.isComplete) {
              setCurrentStep(CREATION_STEPS.length)
            } else {
              // 找到第一个未完成的步骤
              const incompleteStep = CREATION_STEPS.findIndex((step, index) => {
                const field = step.field
                return !completeness.checks[field]
              })
              setCurrentStep(incompleteStep !== -1 ? incompleteStep + 1 : 1)
            }
            
            // 生成导入总结消息
            const personality = aiPersonalityService.getCurrentPersonality()
            let summaryMessage = `${personality.avatar} 文件导入完成！\n\n`
            summaryMessage += `📊 识别到的内容：\n\n`
            
            if (completeness.checks.basic) summaryMessage += `✓ 基本信息（名称、描述）\n`
            if (completeness.checks.worldview) summaryMessage += `✓ 世界观设定\n`
            if (completeness.checks.systems) summaryMessage += `✓ 系统与规则\n`
            if (completeness.checks.regions) summaryMessage += `✓ 地区设定 (${result.data.regions?.length || 0}个)\n`
            if (completeness.checks.characters) summaryMessage += `✓ 角色设定 (${result.data.characters?.length || 0}个)\n`
            
            summaryMessage += `\n完整度：${completeness.percentage}%\n\n`
            
            if (completeness.isComplete) {
              summaryMessage += `世界设定已经相当完整了，你可以直接发布，或继续完善细节。`
              setQuickReplies(['发布到世界库', '继续完善', '查看详情'])
            } else {
              const missingParts = []
              if (!completeness.checks.basic) missingParts.push('基本信息')
              if (!completeness.checks.worldview) missingParts.push('世界观')
              if (!completeness.checks.systems) missingParts.push('系统规则')
              if (!completeness.checks.regions) missingParts.push('地区设定')
              if (!completeness.checks.characters) missingParts.push('角色设定')
              
              summaryMessage += `还缺少：${missingParts.join('、')}\n\n`
              summaryMessage += `我可以帮你补充这些内容，或者你也可以手动添加。`
              setQuickReplies(['AI自动补全', '手动补充', '查看内容'])
            }
            
            setMessages([{
              role: 'assistant',
              content: summaryMessage,
              timestamp: Date.now()
            }])
            
            toast.success(`✨ 导入成功！识别到${completeness.completed}/${completeness.total}项内容`)
            
            // 延迟关闭进度和对话框
            setTimeout(() => {
              setShowImportProgress(false)
              setShowImportDialog(false)
            }, 1500)
            
            return
          }
        } catch (e) {
          console.error('JSON解析错误:', e)
          toast.error(`JSON导入失败: ${e.message}`)
          setShowImportProgress(false)
          setIsAIThinking(false)
          return
        }
      } else if (file.type.includes('pdf')) {
        toast.error('PDF解析功能开发中，请使用JSON或文本文件')
        setShowImportProgress(false)
        setIsAIThinking(false)
        return
      } else {
        // 文本文件
        content = await file.text()
      }

      // 如果不是标准JSON，使用AI分析
      setImportProgress({ percentage: 30, message: '正在使用AI分析内容...', steps: [] })
      
      const prompt = `你是世界设定分析专家。请分析以下导入的内容，提取世界设定信息：

导入内容：
${content.substring(0, 5000)}

请提取并整理成标准格式，返回JSON：
{
  "name": "世界名称",
  "description": "世界描述",
  "summary": "概要",
  "preText": "世界观设定",
  "systems": {},
  "quantitativeSystems": [],
  "regions": [],
  "characters": [],
  "completeness": "评估完整度0-100",
  "missing": ["缺少的部分列表"]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      const jsonMatch = response.match(/\{[\s\S]*\}/)
      
      if (jsonMatch) {
        setImportProgress({ percentage: 80, message: '正在整合分析结果...', steps: [] })
        
        const analyzedData = JSON.parse(jsonMatch[0])
        const completeness = parseInt(analyzedData.completeness) || 0
        const missing = analyzedData.missing || []
        
        // 移除分析字段
        delete analyzedData.completeness
        delete analyzedData.missing
        
        setWorldData(analyzedData)
        
        setImportProgress({ percentage: 100, message: '导入完成！', steps: [] })
        
        if (completeness >= 80) {
          setCurrentStep(CREATION_STEPS.length)
          setMessages([{
            role: 'assistant',
            content: `✨ AI分析完成！\n\n导入内容完整度：${completeness}%\n\n已自动提取所有关键信息，可以直接发布。`,
            timestamp: Date.now()
          }])
          setQuickReplies(['发布到世界库', '保存到我的游戏'])
        } else {
          setMessages([{
            role: 'assistant',
            content: `📊 AI分析完成！\n\n完整度：${completeness}%\n\n已提取信息：\n${Object.keys(analyzedData).filter(k => analyzedData[k]).map(k => `✓ ${k}`).join('\n')}\n\n缺少：\n${missing.map(m => `• ${m}`).join('\n')}\n\n建议使用AI自动补全。`,
            timestamp: Date.now()
          }])
          setQuickReplies(['AI自动补全', '手动补充'])
        }
        
        toast.success('文件分析完成！')
      }
      
      setTimeout(() => {
        setShowImportProgress(false)
        setShowImportDialog(false)
      }, 1500)
      
    } catch (error) {
      console.error('导入失败:', error)
      toast.error(`文件导入失败: ${error.message}`)
      setShowImportProgress(false)
    } finally {
      setIsAIThinking(false)
    }
  }

  // 计算角色完整度
  const calculateCharacterCompleteness = (character) => {
    const hasName = !!(character.name && character.name.trim())
    const hasRole = !!(character.role && character.role.trim())
    const hasBackground = !!(character.background && character.background.trim())
    const hasTraits = !!(character.traits && character.traits.length > 0)
    const hasMotivation = !!(character.motivation && character.motivation.trim())
    
    const fields = [hasName, hasRole, hasBackground, hasTraits, hasMotivation]
    const completed = fields.filter(v => v).length
    const percentage = Math.round((completed / fields.length) * 100)
    
    return {
      hasName,
      hasRole,
      hasBackground,
      hasTraits,
      hasMotivation,
      percentage
    }
  }

  // 检查当前步骤完整度
  const checkStepCompleteness = (step) => {
    const stepId = step.field
    let isComplete = false
    let suggestions = []
    
    switch (stepId) {
      case 'basic':
        isComplete = !!(worldData.name && worldData.description)
        if (!worldData.name) suggestions.push('添加世界名称')
        if (!worldData.description) suggestions.push('添加世界描述')
        if (!worldData.summary) suggestions.push('添加世界概要（可选）')
        break
      
      case 'worldview':
        isComplete = !!(worldData.preText && worldData.preText.length > 50)
        if (!worldData.preText) suggestions.push('添加世界观设定')
        if (worldData.preText && worldData.preText.length < 50) suggestions.push('世界观描述可以更详细一些')
        break
      
      case 'systems':
        isComplete = !!(worldData.systems && Object.keys(worldData.systems).length > 0) || worldData.quantitativeSystems.length > 0
        if (!isComplete) suggestions.push('添加至少一个系统或规则')
        break
      
      case 'regions':
        isComplete = !!(worldData.regions && worldData.regions.length >= 2)
        if (!worldData.regions || worldData.regions.length === 0) suggestions.push('添加地区设定')
        if (worldData.regions && worldData.regions.length < 2) suggestions.push('建议添加至少2-3个地区')
        break
      
      case 'characters':
        isComplete = !!(worldData.characters && worldData.characters.length >= 2)
        if (!worldData.characters || worldData.characters.length === 0) suggestions.push('添加角色设定')
        if (worldData.characters && worldData.characters.length < 2) suggestions.push('建议添加至少2-3个角色')
        // 检查角色完整度
        if (worldData.characters && worldData.characters.length > 0) {
          worldData.characters.forEach((char, i) => {
            if (char.completeness && char.completeness.percentage < 60) {
              suggestions.push(`角色"${char.name || `角色${i+1}`}"信息不够完整`)
            }
          })
        }
        break
      
      default:
        isComplete = true
    }
    
    return {
      isComplete,
      suggestions
    }
  }

  // 检查世界完整度
  const checkWorldCompleteness = (data) => {
    const required = {
      name: !!data.name,
      description: !!data.description,
      preText: !!data.preText,
      systems: data.systems && Object.keys(data.systems).length > 0,
      regions: data.regions && data.regions.length >= 2,
      characters: data.characters && data.characters.length >= 2
    }
    
    const completed = Object.values(required).filter(v => v).length
    const total = Object.keys(required).length
    const percentage = Math.round((completed / total) * 100)
    
    const missing = []
    if (!required.name) missing.push('世界名称')
    if (!required.description) missing.push('世界描述')
    if (!required.preText) missing.push('世界观设定')
    if (!required.systems) missing.push('系统规则')
    if (!required.regions) missing.push('地区设定')
    if (!required.characters) missing.push('角色设定')
    
    return {
      isComplete: percentage >= 80,
      percentage,
      missing
    }
  }

  // 检测未完成的环节并生成引导选项
  const getIncompleteStepsAndSuggestions = () => {
    const incompleteSteps = []
    const suggestions = []
    
    // 检查每个环节
    CREATION_STEPS.forEach((step, index) => {
      const completeness = checkStepCompleteness(step)
      if (!completeness.isComplete) {
        incompleteSteps.push({ step, index: index + 1, suggestions: completeness.suggestions })
      }
    })
    
    // 根据未完成的环节生成建议（格式化为可用的选项格式）
    incompleteSteps.forEach(({ step, index, suggestions: stepSuggestions }) => {
      switch (step.field) {
        case 'basic':
          if (!worldData.name || !worldData.description) {
            suggestions.push('生成基本信息（名称、描述）')
          }
          break
        case 'worldview':
          suggestions.push('生成世界观设定')
          break
        case 'systems':
          suggestions.push('生成系统规则')
          break
        case 'regions':
          suggestions.push('生成地区设定')
          break
        case 'characters':
          suggestions.push('生成角色设定')
          break
      }
    })
    
    return { incompleteSteps, suggestions }
  }

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 bg-dark-900 flex">
      {/* 左侧：对话区域 */}
      <div className="w-1/2 flex flex-col border-r border-dark-700">
        {/* 顶部栏 */}
        <div className="flex-shrink-0 bg-dark-800/80 backdrop-blur-sm border-b border-dark-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-white">AI引导式世界创建</h1>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-dark-300">
                    与AI对话，逐步构建你的世界
                  </p>
                  {lastSaveTime && (
                    <span className="text-xs text-dark-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      已自动保存 {new Date(lastSaveTime).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPersonalitySelector(true)}
                className="px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors text-white text-sm flex items-center gap-1.5"
                title="切换AI性格"
              >
                <span className="text-lg">{currentPersonality.avatar}</span>
                <span>{currentPersonality.name}</span>
              </button>
              <div className="flex items-center gap-1 border-l border-dark-600 pl-2">
                <button
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  className="p-2 hover:bg-dark-600 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="撤销 (Ctrl+Z)"
                >
                  <Undo2 className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  className="p-2 hover:bg-dark-600 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="重做 (Ctrl+Y)"
                >
                  <Redo2 className="w-4 h-4 text-white" />
                </button>
              </div>
              <button
                onClick={() => setShowImportDialog(true)}
                className="px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors text-white text-sm flex items-center gap-1.5"
                title="导入文件"
              >
                <Upload className="w-4 h-4" />
                导入
              </button>
              <button
                onClick={handleAutoCreate}
                disabled={isAutoCreating}
                className="px-3 py-1.5 bg-primary hover:bg-primary-dark rounded-lg transition-colors text-white text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                title="AI一键创建"
              >
                {isAutoCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    创建中...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    一键创建
                  </>
                )}
              </button>
              <button
                onClick={handleExport}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                title="导出世界"
              >
                <Download className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* 对话历史 */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-primary/20 text-white border border-primary/30'
                      : 'bg-dark-800 text-white border border-dark-700'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed text-sm">{msg.content}</p>
                  
                  {/* 如果是AI消息，且是最后一条，且有快捷回复，显示在消息下方 */}
                  {msg.role === 'assistant' && index === messages.length - 1 && quickReplies.length > 0 && !isAIThinking && (
                    <div className="mt-3 pt-3 border-t border-dark-600">
                      {quickReplies.map((reply, replyIndex) => (
                        <button
                          key={replyIndex}
                          onClick={() => handleQuickReply(reply)}
                          className="w-full px-4 py-2 bg-dark-700 hover:bg-primary/20 text-white rounded-lg text-sm transition-all border border-dark-600 hover:border-primary/50 group"
                        >
                          <span className="group-hover:text-primary transition-colors">{reply}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isAIThinking && (
              <div className="flex justify-start">
                <div className="rounded-lg px-4 py-3 bg-dark-800 border border-dark-700">
                  <div className="flex items-center gap-2 text-dark-300">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">AI正在思考...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* 底部：输入区 */}
        <div className="flex-shrink-0 bg-dark-900/95 backdrop-blur-sm border-t border-dark-700 px-4 py-4">
          <div className="max-w-3xl mx-auto space-y-3">
            {/* 输入框 */}
            <div className="flex gap-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="输入你的回答..."
                disabled={isAIThinking}
                className="flex-1 px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary disabled:opacity-50"
              />
              <button
                onClick={handleSendMessage}
                disabled={!userInput.trim() || isAIThinking}
                className="px-6 py-2.5 bg-primary hover:bg-primary-dark rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                发送
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 右侧：世界文档预览 */}
      <div className="w-1/2 flex flex-col bg-dark-950">
        <div className="flex-shrink-0 bg-dark-800/80 backdrop-blur-sm border-b border-dark-700 px-6 py-3">
          <h2 className="text-lg font-bold text-white">世界设定文档</h2>
          <p className="text-xs text-dark-300">实时整理已确定的世界信息</p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* 步骤进度 */}
            <div className="bg-dark-800/50 rounded-lg p-5 border border-dark-700 sticky top-0 z-10 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-white">创建进度</h3>
                <span className="text-sm text-primary font-medium">
                  {currentStep}/{CREATION_STEPS.length} 步骤
                </span>
              </div>
              
              {/* 进度条 */}
              <div className="relative h-3 bg-dark-700 rounded-full overflow-hidden mb-4">
                <div
                  className="absolute top-0 left-0 h-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${(currentStep / CREATION_STEPS.length) * 100}%`,
                    background: currentStep === CREATION_STEPS.length
                      ? 'linear-gradient(90deg, #10b981, #059669)' // 完成时绿色
                      : currentStep >= CREATION_STEPS.length * 0.5
                      ? 'linear-gradient(90deg, #06b6d4, #0891b2)' // 过半时青色
                      : 'linear-gradient(90deg, #8b5cf6, #7c3aed)' // 初始时紫色
                  }}
                />
              </div>
              
              {/* 步骤列表 */}
              <div className="space-y-2">
                {CREATION_STEPS.map((step) => (
                  <div key={step.id} className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                      currentStep > step.id
                        ? 'bg-primary text-white'
                        : currentStep === step.id
                        ? 'bg-primary/30 text-primary border-2 border-primary'
                        : 'bg-dark-700 text-dark-400'
                    }`}>
                      {currentStep > step.id ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        step.id
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${
                        currentStep >= step.id ? 'text-white' : 'text-dark-400'
                      }`}>
                        {step.title}
                      </div>
                      <div className={`text-xs ${
                        currentStep === step.id ? 'text-primary' : 'text-dark-500'
                      }`}>
                        {currentStep === step.id ? '进行中...' : 
                         currentStep > step.id ? '已完成' : '待完成'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 当前步骤完整度提示 */}
            {currentStep > 0 && currentStep < CREATION_STEPS.length && (() => {
              const currentStepData = CREATION_STEPS[currentStep - 1]
              const completeness = checkStepCompleteness(currentStepData)
              
              if (!completeness.isComplete && completeness.suggestions.length > 0) {
                return (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-yellow-400 text-lg">💡</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium mb-2">当前步骤提示</h4>
                        <div className="space-y-1">
                          {completeness.suggestions.map((suggestion, i) => (
                            <div key={i} className="text-sm text-yellow-200 flex items-start gap-2">
                              <span className="text-yellow-400 mt-0.5">•</span>
                              <span>{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }
              
              // 如果完成了，显示成功提示
              if (completeness.isComplete) {
                return (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1">
                        <span className="text-green-200 text-sm">✓ 当前步骤信息已完整</span>
                      </div>
                    </div>
                  </div>
                )
              }
              
              return null
            })()}

            {/* 基本信息 */}
            <div className={`bg-dark-800/50 rounded-lg p-5 border transition-all duration-500 ${
              highlightedFields.includes('name') || highlightedFields.includes('description') || highlightedFields.includes('summary')
                ? 'border-primary shadow-lg shadow-primary/20 animate-pulse'
                : 'border-dark-700'
            }`}>
              <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </span>
                基本信息
                {updatedFields.has('name') && (
                  <span className="text-xs text-green-400 ml-auto">✓ 已更新</span>
                )}
              </h3>
              <div className="space-y-3 text-sm">
                {/* 世界名称 */}
                <div className={`bg-dark-900/50 p-3 rounded-lg transition-all duration-300 ${
                  highlightedFields.includes('name') ? 'ring-2 ring-primary' : ''
                }`}>
                  <span className="text-dark-400 block mb-1">世界名称</span>
                  {editingField?.type === 'name' ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingField.value}
                        onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                        className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveFieldEdit('name', editingField.value)}
                          className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                        >
                          保存
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="text-white font-medium cursor-pointer hover:text-primary transition-colors"
                      onClick={() => setEditingField({ type: 'name', value: worldData.name || '' })}
                    >
                      {worldData.name || '点击添加世界名称'}
                    </div>
                  )}
                </div>
                
                {/* 世界描述 */}
                <div className={`bg-dark-900/50 p-3 rounded-lg transition-all duration-300 ${
                  highlightedFields.includes('description') ? 'ring-2 ring-primary' : ''
                }`}>
                  <span className="text-dark-400 block mb-1">世界描述</span>
                  {editingField?.type === 'description' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingField.value}
                        onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                        className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[100px]"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveFieldEdit('description', editingField.value)}
                          className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                        >
                          保存
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="text-white leading-relaxed cursor-pointer hover:text-primary transition-colors whitespace-pre-wrap"
                      onClick={() => setEditingField({ type: 'description', value: worldData.description || '' })}
                    >
                      {worldData.description || '点击添加世界描述'}
                    </div>
                  )}
                </div>
                
                {/* 世界概要 */}
                <div className={`bg-dark-900/50 p-3 rounded-lg transition-all duration-300 ${
                  highlightedFields.includes('summary') ? 'ring-2 ring-primary' : ''
                }`}>
                  <span className="text-dark-400 block mb-1">世界概要</span>
                  {editingField?.type === 'summary' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingField.value}
                        onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                        className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[100px]"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveFieldEdit('summary', editingField.value)}
                          className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                        >
                          保存
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="text-white leading-relaxed cursor-pointer hover:text-primary transition-colors whitespace-pre-wrap"
                      onClick={() => setEditingField({ type: 'summary', value: worldData.summary || '' })}
                    >
                      {worldData.summary || '点击添加世界概要'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 世界观设定 */}
            <div className={`bg-dark-800/50 rounded-lg p-5 border transition-all duration-500 ${
              highlightedFields.includes('preText')
                ? 'border-primary shadow-lg shadow-primary/20 animate-pulse'
                : 'border-dark-700'
            }`}>
              <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </span>
                世界观设定
                {updatedFields.has('preText') && (
                  <span className="text-xs text-green-400 ml-auto">✓ 已更新</span>
                )}
              </h3>
              <div className="bg-dark-900/50 p-3 rounded-lg">
                {editingField?.type === 'preText' ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingField.value}
                      onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                      className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[300px]"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveFieldEdit('preText', editingField.value)}
                        className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                      >
                        保存
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="text-white text-sm leading-relaxed whitespace-pre-wrap cursor-pointer hover:text-primary transition-colors"
                    onClick={() => setEditingField({ type: 'preText', value: worldData.preText || '' })}
                  >
                    {worldData.preText || '点击添加世界观设定'}
                  </div>
                )}
              </div>
            </div>

            {/* 系统与规则 */}
            <div className={`bg-dark-800/50 rounded-lg p-5 border transition-all duration-500 ${
              highlightedFields.includes('systems')
                ? 'border-primary shadow-lg shadow-primary/20 animate-pulse'
                : 'border-dark-700'
            }`}>
              <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </span>
                系统与规则
                {updatedFields.has('systems') && (
                  <span className="text-xs text-green-400 ml-auto">✓ 已更新</span>
                )}
              </h3>
              <div className="space-y-3">
                {/* 量化系统 */}
                <div className="bg-dark-900/50 p-3 rounded-lg">
                  <span className="text-dark-400 block mb-2 text-sm">量化数值系统</span>
                  <div className="space-y-2">
                    {worldData.quantitativeSystems && worldData.quantitativeSystems.length > 0 && worldData.quantitativeSystems.map((sys, i) => (
                      <div key={i} className="bg-dark-800/50 p-2 rounded">
                        {editingQuantitativeSystemIndex === i ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="系统名称"
                              value={editingField?.value?.name || sys.name || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                              className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                              autoFocus
                            />
                            <input
                              type="number"
                              placeholder="初始值"
                              value={editingField?.value?.initial !== undefined ? editingField.value.initial : (sys.initial !== undefined ? sys.initial : '')}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, initial: parseInt(e.target.value) || 0 } })}
                              className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveFieldEdit('quantitativeSystem', editingField.value)}
                                className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                              >
                                保存
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                              >
                                取消
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="flex items-center justify-between text-sm cursor-pointer hover:bg-dark-700/50 p-2 rounded transition-colors"
                            onClick={() => {
                              setEditingQuantitativeSystemIndex(i)
                              setEditingField({ type: 'quantitativeSystem', value: { name: sys.name || '', initial: sys.initial || 0 } })
                            }}
                          >
                            <span className="text-white">• {sys.name || `系统 ${i + 1}`}</span>
                            <span className="text-primary">初始值: {sys.initial || 0}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* 添加新量化系统 */}
                    {editingQuantitativeSystemIndex === -1 ? (
                      <div className="bg-dark-800/50 p-2 rounded border-2 border-dashed border-primary/50">
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="系统名称"
                            value={editingField?.value?.name || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                            className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            autoFocus
                          />
                          <input
                            type="number"
                            placeholder="初始值"
                            value={editingField?.value?.initial !== undefined ? editingField.value.initial : ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, initial: parseInt(e.target.value) || 0 } })}
                            className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveFieldEdit('quantitativeSystem', editingField.value)}
                              className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                            >
                              保存
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingQuantitativeSystemIndex(-1)
                          setEditingField({ type: 'quantitativeSystem', value: { name: '', initial: 0 } })
                        }}
                        className="w-full px-3 py-2 bg-dark-800/50 hover:bg-dark-700/50 border border-dashed border-dark-600 hover:border-primary/50 rounded text-white text-sm transition-colors"
                      >
                        + 添加量化系统
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 其他系统规则 */}
                <div className="bg-dark-900/50 p-3 rounded-lg">
                  <span className="text-dark-400 block mb-2 text-sm">其他系统</span>
                  <div className="space-y-2">
                    {worldData.systems && Object.keys(worldData.systems).length > 0 && Object.entries(worldData.systems).filter(([key, value]) => value).map(([key, value], i) => (
                      <div key={i} className="bg-dark-800/50 p-2 rounded">
                        {editingSystemKey === key ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="系统名称"
                              value={key}
                              disabled
                              className="w-full bg-dark-700/50 text-dark-400 px-3 py-2 rounded border border-dark-600"
                            />
                            <input
                              type="text"
                              placeholder="系统值"
                              value={editingField?.value !== undefined ? editingField.value : String(value)}
                              onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                              className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveFieldEdit('system', editingField.value)}
                                className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                              >
                                保存
                              </button>
                              <button
                                onClick={() => {
                                  handleSaveFieldEdit('system', null)
                                }}
                                className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-xs"
                              >
                                删除
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                              >
                                取消
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-sm text-white cursor-pointer hover:bg-dark-700/50 p-2 rounded transition-colors"
                            onClick={() => {
                              setEditingSystemKey(key)
                              setEditingField({ type: 'system', value: String(value) })
                            }}
                          >
                            • {key}: {String(value).substring(0, 50)}{String(value).length > 50 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* 添加新系统 */}
                    {editingSystemKey === '__new__' ? (
                      <div className="bg-dark-800/50 p-2 rounded border-2 border-dashed border-primary/50">
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="系统名称（如：声望、资源、战斗等）"
                            value={editingField?.value || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                            className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveFieldEdit('system', editingField.value)}
                              className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                            >
                              保存
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingSystemKey('__new__')
                          setEditingField({ type: 'system', value: '' })
                        }}
                        className="w-full px-3 py-2 bg-dark-800/50 hover:bg-dark-700/50 border border-dashed border-dark-600 hover:border-primary/50 rounded text-white text-sm transition-colors"
                      >
                        + 添加系统
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 规则列表 */}
                <div className="bg-dark-900/50 p-3 rounded-lg">
                  <span className="text-dark-400 block mb-2 text-sm">规则说明</span>
                  <div className="space-y-2">
                    {worldData.rules && worldData.rules.length > 0 && worldData.rules.map((rule, i) => (
                      <div key={i} className="bg-dark-800/50 p-2 rounded">
                        {editingRuleIndex === i ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="规则名称"
                              value={editingField?.value?.name || rule.name || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                              className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                              autoFocus
                            />
                            <textarea
                              placeholder="规则描述"
                              value={editingField?.value?.description || rule.description || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, description: e.target.value } })}
                              className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveFieldEdit('rule', editingField.value)}
                                className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                              >
                                保存
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                              >
                                取消
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-sm text-white cursor-pointer hover:bg-dark-700/50 p-2 rounded transition-colors"
                            onClick={() => {
                              setEditingRuleIndex(i)
                              setEditingField({ type: 'rule', value: { name: rule.name || '', description: rule.description || '' } })
                            }}
                          >
                            <div className="font-medium mb-1">• {rule.name || `规则 ${i + 1}`}</div>
                            {rule.description && (
                              <div className="text-dark-300 text-xs ml-4 whitespace-pre-wrap">{rule.description}</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* 添加新规则 */}
                    {editingRuleIndex === -1 ? (
                      <div className="bg-dark-800/50 p-2 rounded border-2 border-dashed border-primary/50">
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="规则名称"
                            value={editingField?.value?.name || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                            className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            autoFocus
                          />
                          <textarea
                            placeholder="规则描述"
                            value={editingField?.value?.description || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, description: e.target.value } })}
                            className="w-full bg-dark-700 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveFieldEdit('rule', editingField.value)}
                              className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                            >
                              保存
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingRuleIndex(-1)
                          setEditingField({ type: 'rule', value: { name: '', description: '' } })
                        }}
                        className="w-full px-3 py-2 bg-dark-800/50 hover:bg-dark-700/50 border border-dashed border-dark-600 hover:border-primary/50 rounded text-white text-sm transition-colors"
                      >
                        + 添加规则
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 地区设定 */}
            <div className={`bg-dark-800/50 rounded-lg p-5 border transition-all duration-500 ${
              highlightedFields.includes('regions')
                ? 'border-primary shadow-lg shadow-primary/20 animate-pulse'
                : 'border-dark-700'
            }`}>
              <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </span>
                地区设定
                {updatedFields.has('regions') && (
                  <span className="text-xs text-green-400 ml-auto">✓ 已更新</span>
                )}
              </h3>
              <div className="space-y-3">
                {worldData.regions && worldData.regions.length > 0 && worldData.regions.map((region, i) => (
                  <div key={i} className="bg-dark-900/50 p-3 rounded-lg">
                    {editingRegionIndex === i ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="地区名称"
                          value={editingField?.value?.name || region.name || ''}
                          onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                          className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                          autoFocus
                        />
                        <textarea
                          placeholder="地区描述"
                          value={editingField?.value?.description || region.description || ''}
                          onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, description: e.target.value } })}
                          className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[100px]"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveFieldEdit('region', editingField.value)}
                            className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                          >
                            保存
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="cursor-pointer hover:bg-dark-800/50 p-2 rounded transition-colors"
                        onClick={() => {
                          setEditingRegionIndex(i)
                          setEditingField({ type: 'region', value: { name: region.name || '', description: region.description || '' } })
                        }}
                      >
                        <span className="text-white font-medium text-sm block">
                          {region.name || `地区 ${i + 1}`}
                        </span>
                        {region.description && (
                          <p className="text-dark-300 text-sm mt-2 whitespace-pre-wrap">{region.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* 添加新地区 */}
                {editingRegionIndex === -1 ? (
                  <div className="bg-dark-900/50 p-3 rounded-lg border-2 border-dashed border-primary/50">
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="地区名称"
                        value={editingField?.value?.name || ''}
                        onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                        className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                        autoFocus
                      />
                      <textarea
                        placeholder="地区描述"
                        value={editingField?.value?.description || ''}
                        onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, description: e.target.value } })}
                        className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[100px]"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveFieldEdit('region', editingField.value)}
                          className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                        >
                          保存
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingRegionIndex(-1)
                      setEditingField({ type: 'region', value: { name: '', description: '' } })
                    }}
                    className="w-full px-3 py-2 bg-dark-900/50 hover:bg-dark-800/50 border border-dashed border-dark-600 hover:border-primary/50 rounded text-white text-sm transition-colors"
                  >
                    + 添加地区
                  </button>
                )}
              </div>
            </div>
            

            {/* 角色设定 */}
            <div className={`bg-dark-800/50 rounded-lg p-5 border transition-all duration-500 ${
              highlightedFields.includes('characters')
                ? 'border-primary shadow-lg shadow-primary/20 animate-pulse'
                : 'border-dark-700'
            }`}>
              <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </span>
                角色设定
                {updatedFields.has('characters') && (
                  <span className="text-xs text-green-400">✓ 已更新</span>
                )}
                <span className="text-xs text-dark-400 font-normal ml-auto">
                  {worldData.characters ? worldData.characters.length : 0} 个角色
                </span>
              </h3>
              <div className="space-y-3">
                {worldData.characters && worldData.characters.length > 0 && worldData.characters.map((character, i) => {
                  const previewCharacter = editingCharacterIndex === i && editingField?.type === 'character'
                    ? { ...character, ...editingField.value }
                    : character
                  const completeness = previewCharacter?.completeness || calculateCharacterCompleteness(previewCharacter || {})
                  
                  return (
                  <div key={i} className="bg-dark-900/50 rounded-lg p-4 border border-dark-700/50 hover:border-primary/30 transition-colors">
                      {/* 角色头部 */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-bold text-base">
                              {character.name || `角色 ${i + 1}`}
                            </span>
                            {character.role && (
                              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded border border-primary/20">
                                {character.role}
                              </span>
                            )}
                          </div>
                          
                          {/* 完整度进度条 */}
                          {completeness && (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="flex-1 h-1.5 bg-dark-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full transition-all duration-300"
                                  style={{
                                    width: `${completeness.percentage || 0}%`,
                                    background: completeness.percentage >= 80
                                      ? 'linear-gradient(90deg, #10b981, #059669)'
                                      : completeness.percentage >= 50
                                      ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                                      : 'linear-gradient(90deg, #ef4444, #dc2626)'
                                  }}
                                />
                              </div>
                              <span className={`text-xs font-medium ${
                                completeness.percentage >= 80 ? 'text-green-400' :
                                completeness.percentage >= 50 ? 'text-yellow-400' :
                                'text-red-400'
                              }`}>
                                {completeness.percentage}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {CHARACTER_PROGRESS_FIELDS.map(field => {
                          const filled = completeness?.[field.key]
                          return (
                            <span
                              key={field.key}
                              className={`px-2 py-0.5 text-xs rounded-full border transition-all ${
                                filled
                                  ? 'bg-primary/10 text-primary border-primary shadow-[0_0_12px_rgba(249,115,22,0.2)]'
                                  : 'bg-dark-800 text-dark-400 border-dashed border-dark-600'
                              }`}
                            >
                              {filled ? '✓ ' : ''}{field.label}
                            </span>
                          )
                        })}
                      </div>

                      {/* 角色详细信息 */}
                      {editingCharacterIndex === i ? (
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {CHARACTER_PROGRESS_FIELDS.map(field => {
                              const filled = completeness?.[field.key]
                              return (
                                <span
                                  key={field.key}
                                  className={`px-2 py-0.5 text-xs rounded-full border transition-all ${
                                    filled
                                      ? 'bg-primary/10 text-primary border-primary shadow-[0_0_12px_rgba(249,115,22,0.2)]'
                                      : 'bg-dark-800 text-dark-400 border-dashed border-dark-600'
                                  }`}
                                >
                                  {filled ? '✓ ' : ''}{field.label}
                                </span>
                              )
                            })}
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">角色名称</label>
                            <input
                              type="text"
                              value={editingField?.value?.name || character.name || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">职业/身份</label>
                            <input
                              type="text"
                              value={editingField?.value?.role || character.role || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, role: e.target.value } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">背景</label>
                            <textarea
                              value={editingField?.value?.background || character.background || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, background: e.target.value } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                            />
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">特点（用逗号分隔）</label>
                            <input
                              type="text"
                              value={editingField?.value?.traits ? editingField.value.traits.join(', ') : (character.traits ? character.traits.join(', ') : '')}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, traits: e.target.value.split(',').map(t => t.trim()).filter(t => t) } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">目标动机</label>
                            <textarea
                              value={editingField?.value?.motivation || character.motivation || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, motivation: e.target.value } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                            />
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">关系</label>
                            <textarea
                              value={editingField?.value?.relationships || character.relationships || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, relationships: e.target.value } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[60px]"
                            />
                          </div>
                          <div>
                            <label className="text-dark-400 text-xs block mb-1">描述</label>
                            <textarea
                              value={editingField?.value?.description || character.description || ''}
                              onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, description: e.target.value } })}
                              className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                            />
                          </div>
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => handleSaveFieldEdit('character', editingField.value)}
                              className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                            >
                              保存
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm">
                          {/* 背景 */}
                          {previewCharacter.background && (
                            <div className="bg-dark-800/50 p-2 rounded">
                              <span className="text-dark-400 text-xs block mb-1">📖 背景</span>
                              <p className="text-white leading-relaxed whitespace-pre-wrap">{previewCharacter.background}</p>
                            </div>
                          )}
                          
                          {/* 特点 */}
                          {previewCharacter.traits && previewCharacter.traits.length > 0 && (
                            <div className="bg-dark-800/50 p-2 rounded">
                              <span className="text-dark-400 text-xs block mb-1">✨ 特点</span>
                              <div className="flex flex-wrap gap-1.5">
                                {previewCharacter.traits.map((trait, ti) => (
                                  <span key={ti} className="text-xs bg-dark-700 text-white px-2 py-1 rounded">
                                    {trait}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* 目标动机 */}
                          {previewCharacter.motivation && (
                            <div className="bg-dark-800/50 p-2 rounded">
                              <span className="text-dark-400 text-xs block mb-1">🎯 目标动机</span>
                              <p className="text-white leading-relaxed whitespace-pre-wrap">{previewCharacter.motivation}</p>
                            </div>
                          )}
                          
                          {/* 关系 */}
                          {previewCharacter.relationships && (
                            <div className="bg-dark-800/50 p-2 rounded">
                              <span className="text-dark-400 text-xs block mb-1">🔗 关系</span>
                              <p className="text-white leading-relaxed whitespace-pre-wrap">{previewCharacter.relationships}</p>
                            </div>
                          )}
                          
                          {/* 通用描述（如果没有详细信息） */}
                          {previewCharacter.description && !previewCharacter.background && !previewCharacter.motivation && (
                            <div className="bg-dark-800/50 p-2 rounded">
                              <p className="text-dark-300 leading-relaxed whitespace-pre-wrap">{previewCharacter.description}</p>
                            </div>
                          )}
                          
                          {/* 编辑按钮 */}
                          <button
                            onClick={() => {
                              setEditingCharacterIndex(i)
                              setEditingField({ 
                                type: 'character', 
                                value: { 
                                  name: character.name || '',
                                  role: character.role || '',
                                  background: character.background || '',
                                  traits: character.traits || [],
                                  motivation: character.motivation || '',
                                  relationships: character.relationships || '',
                                  description: character.description || ''
                                } 
                              })
                            }}
                            className="w-full mt-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs transition-colors"
                          >
                            编辑角色信息
                          </button>
                        </div>
                      )}
                      
                      {/* 缺失信息提示 */}
                      {completeness && completeness.percentage < 80 && (
                        <div className="mt-3 pt-3 border-t border-dark-700">
                          <div className="flex items-start gap-2 text-xs">
                            <span className="text-yellow-400">💡</span>
                            <div className="flex-1">
                              <span className="text-dark-400">可以补充：</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {!completeness.hasName && (
                                  <span className="text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">名字</span>
                                )}
                                {!completeness.hasRole && (
                                  <span className="text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">职业</span>
                                )}
                                {!completeness.hasBackground && (
                                  <span className="text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">背景</span>
                                )}
                                {!completeness.hasTraits && (
                                  <span className="text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">特点</span>
                                )}
                                {!completeness.hasMotivation && (
                                  <span className="text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">动机</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )})}
                  
                  {/* 添加新角色 */}
                  {editingCharacterIndex === -1 ? (
                    <div className="bg-dark-900/50 rounded-lg p-4 border-2 border-dashed border-primary/50">
                      {editingField?.type === 'character' && (() => {
                        const previewCompleteness = calculateCharacterCompleteness(editingField?.value || {})
                        return (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {CHARACTER_PROGRESS_FIELDS.map(field => {
                              const filled = previewCompleteness?.[field.key]
                              return (
                                <span
                                  key={field.key}
                                  className={`px-2 py-0.5 text-xs rounded-full border transition-all ${
                                    filled
                                      ? 'bg-primary/10 text-primary border-primary shadow-[0_0_12px_rgba(249,115,22,0.2)]'
                                      : 'bg-dark-800 text-dark-400 border-dashed border-dark-600'
                                  }`}
                                >
                                  {filled ? '✓ ' : ''}{field.label}
                                </span>
                              )
                            })}
                          </div>
                        )
                      })()}
                      <div className="space-y-3">
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">角色名称</label>
                          <input
                            type="text"
                            placeholder="角色名称"
                            value={editingField?.value?.name || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, name: e.target.value } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">职业/身份</label>
                          <input
                            type="text"
                            placeholder="职业/身份"
                            value={editingField?.value?.role || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, role: e.target.value } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">背景</label>
                          <textarea
                            placeholder="角色背景"
                            value={editingField?.value?.background || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, background: e.target.value } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                          />
                        </div>
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">特点（用逗号分隔）</label>
                          <input
                            type="text"
                            placeholder="特点1, 特点2, 特点3"
                            value={editingField?.value?.traits ? editingField.value.traits.join(', ') : ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, traits: e.target.value.split(',').map(t => t.trim()).filter(t => t) } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">目标动机</label>
                          <textarea
                            placeholder="角色的目标和动机"
                            value={editingField?.value?.motivation || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, motivation: e.target.value } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                          />
                        </div>
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">关系</label>
                          <textarea
                            placeholder="与其他角色的关系"
                            value={editingField?.value?.relationships || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, relationships: e.target.value } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[60px]"
                          />
                        </div>
                        <div>
                          <label className="text-dark-400 text-xs block mb-1">描述</label>
                          <textarea
                            placeholder="角色描述"
                            value={editingField?.value?.description || ''}
                            onChange={(e) => setEditingField({ ...editingField, value: { ...editingField?.value, description: e.target.value } })}
                            className="w-full bg-dark-800 text-white px-3 py-2 rounded border border-dark-600 focus:border-primary focus:outline-none min-h-[80px]"
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => handleSaveFieldEdit('character', editingField.value)}
                            className="px-3 py-1 bg-primary hover:bg-primary-dark rounded text-white text-xs"
                          >
                            保存
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded text-white text-xs"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingCharacterIndex(-1)
                        setEditingField({ 
                          type: 'character', 
                          value: { 
                            name: '',
                            role: '',
                            background: '',
                            traits: [],
                            motivation: '',
                            relationships: '',
                            description: ''
                          } 
                        })
                      }}
                      className="w-full px-3 py-2 bg-dark-900/50 hover:bg-dark-800/50 border border-dashed border-dark-600 hover:border-primary/50 rounded text-white text-sm transition-colors"
                    >
                      + 添加角色
                    </button>
                  )}
                </div>
            </div>

            {/* 完整度提示 */}
            {currentStep === CREATION_STEPS.length && (
              <div className="bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg p-5 border border-primary/30">
                <h3 className="text-base font-bold text-white mb-2">✨ 世界创建完成！</h3>
                <p className="text-dark-300 text-sm mb-4">
                  你的世界设定已经完成，现在可以选择发布或保存。
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFinishCreation(true)}
                    className="flex-1 py-2 bg-primary hover:bg-primary-dark rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    发布到世界库
                  </button>
                  <button
                    onClick={() => handleFinishCreation(false)}
                    className="flex-1 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    保存到我的游戏
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI性格选择器 */}
      {showPersonalitySelector && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col border border-dark-600">
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div>
                <h3 className="text-xl font-bold text-white">选择AI助手性格</h3>
                <p className="text-sm text-dark-300 mt-1">每位助手都有独特的人设和说话风格</p>
              </div>
              <button
                onClick={() => setShowPersonalitySelector(false)}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aiPersonalityService.getAvailablePersonalities().map((personality) => (
                <button
                  key={personality.id}
                  onClick={() => {
                    aiPersonalityService.setPersonality(personality.id)
                    setCurrentPersonality(personality)
                    setShowPersonalitySelector(false)
                    toast.success(`已切换到 ${personality.name} ${personality.avatar}`)
                    // 重新初始化对话
                    initConversation()
                  }}
                  className={`p-5 rounded-lg border-2 transition-all text-left hover:scale-[1.02] ${
                    currentPersonality.id === personality.id
                      ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20'
                      : 'border-dark-600 bg-dark-700/50 hover:border-primary/50'
                  }`}
                >
                  {/* 头部 */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{personality.avatar}</span>
                      <div>
                        <div className="text-white font-bold text-lg">{personality.name}</div>
                        <div className="text-xs text-primary/80 font-medium">{personality.style}</div>
                      </div>
                    </div>
                    {currentPersonality.id === personality.id && (
                      <div className="flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                    )}
                  </div>
                  
                  {/* 背景 */}
                  <div className="mb-3">
                    <div className="text-xs text-dark-400 mb-1">背景设定</div>
                    <div className="text-sm text-dark-200 leading-relaxed">{personality.background}</div>
                  </div>
                  
                  {/* 性格 */}
                  <div className="mb-3">
                    <div className="text-xs text-dark-400 mb-1">性格特点</div>
                    <div className="text-sm text-dark-200 leading-relaxed">{personality.personality}</div>
                  </div>
                  
                  {/* 示例对话 */}
                  <div className="bg-dark-800/70 p-3 rounded-lg border border-dark-600">
                    <div className="text-xs text-dark-400 mb-1.5">开场白</div>
                    <div className="text-sm text-white/90 italic leading-relaxed">"{personality.greeting}"</div>
                  </div>
                  
                  {/* 说话习惯标签 */}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {personality.speakingHabits.slice(0, 2).map((habit, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-dark-600/50 text-dark-300 rounded">
                        {habit.substring(0, 10)}...
                      </span>
                    ))}
                  </div>
                </button>
              ))}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="p-4 bg-dark-900/50 rounded-lg text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-primary text-lg">💡</span>
                  <div className="font-medium text-white">独特人设</div>
                </div>
                <div className="text-dark-300 leading-relaxed">
                  每个AI助手都有独特的背景、性格和说话方式，他们会像真实的人一样与你对话，让创作过程更有趣。
                </div>
              </div>
              
              <div className="p-4 bg-dark-900/50 rounded-lg text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-primary text-lg">🎭</span>
                  <div className="font-medium text-white">避免重复</div>
                </div>
                <div className="text-dark-300 leading-relaxed">
                  每个角色都有丰富的词汇库，不会机械地重复"很好"、"不错"等词汇，每次回复都有新鲜感。
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm">
              <div className="flex items-start gap-2 text-yellow-200">
                <span className="text-yellow-400 mt-0.5">⚠️</span>
                <div>
                  <span className="font-medium">注意：</span>
                  切换AI性格会清空当前对话并重新开始。建议在创作初期选择合适的性格。
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 导入进度显示 */}
      {showImportProgress && importProgress && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-dark-800 rounded-xl p-8 max-w-md w-full mx-4 border border-primary/30 shadow-2xl">
            {/* 标题 */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-primary animate-pulse" />
              <h3 className="text-xl font-bold text-white">正在导入世界设定</h3>
            </div>
            
            {/* 进度条 */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-dark-300">{importProgress.message}</span>
                <span className="text-sm font-bold text-primary">{importProgress.percentage}%</span>
              </div>
              <div className="w-full h-2 bg-dark-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-500 ease-out"
                  style={{ width: `${importProgress.percentage}%` }}
                />
              </div>
            </div>
            
            {/* 步骤列表 */}
            {importProgress.steps && importProgress.steps.length > 0 && (
              <div className="space-y-2">
                {importProgress.steps.map((step, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      step.completed ? 'bg-green-500' : 'bg-dark-600'
                    }`}>
                      {step.completed && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className={step.completed ? 'text-green-400' : 'text-dark-400'}>
                      {step.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {/* 完成信息 */}
            {importProgress.percentage === 100 && (
              <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">导入完成！</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 导入对话框 */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 max-w-lg w-full mx-4 border border-dark-600">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">导入世界设定文件</h3>
              <button
                onClick={() => setShowImportDialog(false)}
                className="p-1 hover:bg-dark-700 rounded transition-colors"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-dark-300 text-sm mb-4">
              支持导入 JSON、TXT、Word 等格式的文件。AI将自动分析并提取世界设定信息。
            </p>

            <div className="border-2 border-dashed border-dark-600 rounded-lg p-8 text-center mb-4 hover:border-primary/50 transition-colors">
              <input
                type="file"
                id="file-upload"
                accept=".json,.txt,.doc,.docx,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleFileImport(file)
                  }
                }}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <div className="w-16 h-16 bg-dark-700 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <p className="text-white font-medium mb-1">点击选择文件</p>
                  <p className="text-dark-400 text-xs">或将文件拖拽到这里</p>
                </div>
                <div className="flex gap-2 text-xs text-dark-400">
                  <span>JSON</span>
                  <span>•</span>
                  <span>TXT</span>
                  <span>•</span>
                  <span>Word</span>
                  <span>•</span>
                  <span>PDF</span>
                </div>
              </label>
            </div>

            <div className="bg-dark-900/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-start gap-2 text-dark-300">
                <span className="text-primary mt-0.5">✓</span>
                <span>JSON格式：自动识别标准格式，快速导入</span>
              </div>
              <div className="flex items-start gap-2 text-dark-300">
                <span className="text-primary mt-0.5">✓</span>
                <span>文本文件：AI智能分析，提取关键信息</span>
              </div>
              <div className="flex items-start gap-2 text-dark-300">
                <span className="text-primary mt-0.5">✓</span>
                <span>完整度检测：自动评估并提示缺失内容</span>
              </div>
              <div className="flex items-start gap-2 text-dark-300">
                <span className="text-primary mt-0.5">✓</span>
                <span>AI补全：一键自动补全所有缺失信息</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const normalizeCharacterData = (character) => {
  if (!character || typeof character !== 'object') return character
  
  const normalized = { ...character }
  const roleKeys = ['role', 'identity', 'job', 'occupation', 'position', 'title', 'profession', 'status']
  
  const extractRoleFromText = (text) => {
    if (!text || typeof text !== 'string') return ''
    const patterns = [
      /(?:身份|职业)[：:]\s*([^，。,。；;]+)/i,
      /(?:是|作为|身为|担任)(?:一名|一个|一位)?([^，。,。；;]+)/i
    ]
    for (const pattern of patterns) {
      const match = text.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return ''
  }
  
  if (!normalized.role) {
    for (const key of roleKeys) {
      if (normalized[key] && typeof normalized[key] === 'string') {
        const candidate = normalized[key].trim()
        if (candidate) {
          normalized.role = candidate
          if (key !== 'role') {
            delete normalized[key]
          }
          break
        }
      }
    }
  } else if (typeof normalized.role === 'string') {
    normalized.role = normalized.role.trim()
  }
  
  if (!normalized.role && typeof normalized.description === 'string') {
    const inferredRole = extractRoleFromText(normalized.description)
    if (inferredRole) {
      normalized.role = inferredRole
    }
  }
  
  if (typeof normalized.role === 'string') {
    normalized.role = normalized.role.replace(/^[：:\s-]+/, '').trim()
  }
  
  if (Array.isArray(normalized.traits)) {
    normalized.traits = normalized.traits
      .filter(trait => typeof trait === 'string' && trait.trim())
      .map(trait => trait.trim())
  } else if (typeof normalized.traits === 'string') {
    normalized.traits = normalized.traits
      .split(/[,，、]/)
      .map(trait => trait.trim())
      .filter(Boolean)
  } else if (normalized.traits) {
    normalized.traits = [String(normalized.traits).trim()].filter(Boolean)
  } else {
    normalized.traits = normalized.traits || []
  }
  
  return normalized
}

